import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BaseComponent } from '../base/base.component';
import { CheckboxComponent } from '../core/controls/checkbox.component';
import { RadioButtonComponent } from '../core/controls/radio-button.component';
import { DatePickerComponent } from '../core/controls/date-picker.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { HelpTextComponent } from '../core/controls/help-text.component';
import { InputComponent } from '../core/controls/input.component';
import { SelectComponent } from '../core/controls/select.component';
import { RangeComponent } from '../core/controls/range.component';
import {
  applyFinancialOptionsFromApi,
  applyQueryWorkFromApi,
  applyWcClaimTypesSelection,
  applyWcInjuryParametersFromApi,
} from './query-extended-params.form';
import {
  QUERY_CLAIM_FLAG_FIELD_DEFS,
  queryClaimFlagFormKeyForCode,
  queryClaimFlagFromStorage,
  queryClaimFlagSelectOptions,
  QueryClaimFlagFormKey,
} from './query-claim-flags.helpers';
import { isClaimTypeLobSectionVisible, ParameterSideMenuKey } from './query-parameter-side-menu';
import {
  formatWcSubtypeOptionLabel,
  normalizeWcLobItem,
  WC_LEGACY_ALL_SUBTYPES_VALUE,
  wcClaimTypeOptionTrack,
} from './wc-claim-type-labels';
import {
  BankingOption,
  LocationUnitItem,
  PaycodeItem,
  PaymentOption,
  PaymentOptionsResponse,
  QueryFlagItem,
  StateItem,
  TimeTrackingOption,
  QueryWcInjuryCodeItem,
} from '../../models/api/query/query-parameters';
import { LobItem } from '../../models/api/reports/lob-filter';
import { CorpStructNodeItem } from '../../models/api/reports/location-filter';
import { QueryDisabilityLobComponent } from './query-disability-lob.component';
import { QueryMajorLiabilityLobComponent } from './query-major-liability-lob.component';
import { LocationAccountUnitsPanelComponent } from './location-account-units-panel.component';
import { QueryVirtualDualListComponent } from './query-virtual-dual-list.component';
import { TreeComponent, TreeNode } from '../core/tree/tree.component';

/** Loss / jurisdiction / payroll state dual-list controls (same form keys as extended params). */
type StateCodesKind = 'lossStateCodes' | 'jurisStateCodes' | 'payrollStateCodes';

/** Legacy WC query wizard sub-steps (Workers' Comp sidebar). */
type WcLobSubTab = 'claimType' | 'injuryCodes' | 'icdCodes' | 'workStatus';

/** Legacy AdvancedMajorDisabilityTab.ascx: Miscellaneous first, then Claim Type/Status, ICD Codes, Work Status. */
type DsLobSubTab = 'misc' | 'claim' | 'icdCodes' | 'workStatus';

/** Legacy Advanced → Claims: General / States / Vendors (same states UI as Simple → States). */
type ClaimsSubTab = 'general' | 'states' | 'vendors';

/** Legacy `AdvancedPaymentsTab.ascx`: General vs Pay Codes (`QueryWizardAdvancedPayments1` / `2`). */
type AdvPaymentsSubTab = 'general' | 'payCodes';

@Component({
  selector: 'app-query-parameter-panel-host',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SelectComponent,
    DatePickerComponent,
    RangeComponent,
    InputComponent,
    HelpTextComponent,
    CheckboxComponent,
    RadioButtonComponent,
    ButtonComponent,
    QueryDisabilityLobComponent,
    QueryMajorLiabilityLobComponent,
    LocationAccountUnitsPanelComponent,
    QueryVirtualDualListComponent,
    TreeComponent,
  ],
  template: `
    <!-- Native HTML select/input in dual-list and ICD search rows: listbox semantics and lightweight inline fields; borders use --sed-black-60 per global form contrast. -->
    <div class="tw-space-y-4">
      <ng-template #claimStatesDualListsTpl>
        @if (statesLoading) {
          <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading states…</p>
        } @else {
          <div class="tw-overflow-auto">
            <table class="tw-w-full tw-text-sm tw-border-collapse">
              <thead>
                <tr>
                  <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Available States of Jurisdiction</th>
                  <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                  <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Restrict to Following States</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="tw-align-top tw-pr-2">
                    <label class="tw-sr-only" for="qp-states-juris-avail"
                      >Available states of jurisdiction</label
                    >
                    <select
                      id="qp-states-juris-avail"
                      #availJuris
                      multiple
                      class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    >
                      @for (o of availableStateOptions('jurisStateCodes'); track o.value) {
                        <option [value]="o.value">{{ o.label }}</option>
                      }
                    </select>
                  </td>
                  <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                      <app-button
                        label="→"
                        ariaLabel="Add selected jurisdiction states"
                        (click)="addSelectedStates('jurisStateCodes', availJuris)"
                      />
                      <app-button
                        label="⇒"
                        ariaLabel="Add all jurisdiction states"
                        (click)="addAllStates('jurisStateCodes')"
                      />
                      <app-button
                        label="←"
                        ariaLabel="Remove selected jurisdiction states"
                        (click)="removeSelectedStates('jurisStateCodes', restJuris)"
                      />
                      <app-button
                        label="⇐"
                        ariaLabel="Remove all jurisdiction states"
                        (click)="removeAllStates('jurisStateCodes')"
                      />
                    </div>
                  </td>
                  <td class="tw-align-top tw-pl-2">
                    <label class="tw-sr-only" for="qp-states-juris-restrict"
                      >Restricted states of jurisdiction</label
                    >
                    <select
                      id="qp-states-juris-restrict"
                      #restJuris
                      multiple
                      class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    >
                      @for (o of restrictedStateOptions('jurisStateCodes'); track o.value) {
                        <option [value]="o.value">{{ o.label }}</option>
                      }
                    </select>
                  </td>
                </tr>
                <tr>
                  <th scope="col" class="tw-text-left tw-font-semibold tw-pt-4 tw-pb-2 tw-pr-2">Available States of Loss</th>
                  <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                  <th scope="col" class="tw-text-left tw-font-semibold tw-pt-4 tw-pb-2 tw-pl-2">Restrict to Following States</th>
                </tr>
                <tr>
                  <td class="tw-align-top tw-pr-2 tw-pt-1">
                    <label class="tw-sr-only" for="qp-states-loss-avail"
                      >Available states of loss</label
                    >
                    <select
                      id="qp-states-loss-avail"
                      #availLoss
                      multiple
                      class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    >
                      @for (o of availableStateOptions('lossStateCodes'); track o.value) {
                        <option [value]="o.value">{{ o.label }}</option>
                      }
                    </select>
                  </td>
                  <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                      <app-button
                        label="→"
                        ariaLabel="Add selected loss states"
                        (click)="addSelectedStates('lossStateCodes', availLoss)"
                      />
                      <app-button
                        label="⇒"
                        ariaLabel="Add all loss states"
                        (click)="addAllStates('lossStateCodes')"
                      />
                      <app-button
                        label="←"
                        ariaLabel="Remove selected loss states"
                        (click)="removeSelectedStates('lossStateCodes', restLoss)"
                      />
                      <app-button
                        label="⇐"
                        ariaLabel="Remove all loss states"
                        (click)="removeAllStates('lossStateCodes')"
                      />
                    </div>
                  </td>
                  <td class="tw-align-top tw-pl-2 tw-pt-1">
                    <label class="tw-sr-only" for="qp-states-loss-restrict"
                      >Restricted states of loss</label
                    >
                    <select
                      id="qp-states-loss-restrict"
                      #restLoss
                      multiple
                      class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    >
                      @for (o of restrictedStateOptions('lossStateCodes'); track o.value) {
                        <option [value]="o.value">{{ o.label }}</option>
                      }
                    </select>
                  </td>
                </tr>
                @if (showPayrollStatesInClaimStates) {
                  <tr>
                    <th scope="col" class="tw-text-left tw-font-semibold tw-pt-4 tw-pb-2 tw-pr-2">Available States of Payroll</th>
                    <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                    <th scope="col" class="tw-text-left tw-font-semibold tw-pt-4 tw-pb-2 tw-pl-2">Restrict to Following States</th>
                  </tr>
                  <tr>
                    <td class="tw-align-top tw-pr-2 tw-pt-1">
                      <label class="tw-sr-only" for="qp-states-payroll-avail"
                        >Available states of payroll</label
                      >
                      <select
                        id="qp-states-payroll-avail"
                        #availPayroll
                        multiple
                        class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                      >
                        @for (o of availableStateOptions('payrollStateCodes'); track o.value) {
                          <option [value]="o.value">{{ o.label }}</option>
                        }
                      </select>
                    </td>
                    <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                      <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                        <app-button
                          label="→"
                          ariaLabel="Add selected payroll states"
                          (click)="addSelectedStates('payrollStateCodes', availPayroll)"
                        />
                        <app-button
                          label="⇒"
                          ariaLabel="Add all payroll states"
                          (click)="addAllStates('payrollStateCodes')"
                        />
                        <app-button
                          label="←"
                          ariaLabel="Remove selected payroll states"
                          (click)="removeSelectedStates('payrollStateCodes', restPayroll)"
                        />
                        <app-button
                          label="⇐"
                          ariaLabel="Remove all payroll states"
                          (click)="removeAllStates('payrollStateCodes')"
                        />
                      </div>
                    </td>
                    <td class="tw-align-top tw-pl-2 tw-pt-1">
                      <label class="tw-sr-only" for="qp-states-payroll-restrict"
                        >Restricted states of payroll</label
                      >
                      <select
                        id="qp-states-payroll-restrict"
                        #restPayroll
                        multiple
                        class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                      >
                        @for (o of restrictedStateOptions('payrollStateCodes'); track o.value) {
                          <option [value]="o.value">{{ o.label }}</option>
                        }
                      </select>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </ng-template>

      <!-- Legacy QueryWizardSimpleFlags / QueryWizardAdvancedFlags: tri-state dropdown per flag. -->
      <ng-template #claimFlagsPanelTpl>
        <div class="tw-max-w-[720px]">
          <h3 class="tw-font-semibold tw-text-lg tw-pb-2">Claim Flags</h3>
          @if (flagsLoading) {
            <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading flags…</p>
          } @else {
            <div class="tw-flex tw-flex-col tw-gap-3">
              @for (def of claimFlagFieldDefs; track def.formKey) {
                <div
                  class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-[10rem_1fr] tw-gap-2 tw-items-center"
                >
                  <span class="tw-font-semibold">{{ def.label }}:</span>
                  <app-select
                    [control]="claimFlagControl(def.formKey)"
                    label=""
                    [screenReaderLabel]="def.label + ' claim flag filter'"
                    [options]="claimFlagOptions(def.label)"
                    optionLabel="label"
                    optionValue="value"
                    [filter]="false"
                    [showClear]="false"
                    [virtualScroll]="false"
                    [showHelpText]="false"
                  />
                </div>
              }
            </div>
          }
        </div>
      </ng-template>

      <!-- Legacy QueryWizardSimpleTime / QueryWizardAdvancedTime: dual lists (→⇒←⇐); dates only on Advanced page. -->
      <ng-template #timeBenefitDualLists>
        <div class="tw-space-y-8">
          <div class="tw-overflow-auto">
            <table class="tw-w-full tw-text-sm tw-border-collapse">
              <thead>
                <tr>
                  <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Available Benefit Types:</th>
                  <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                  <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Restrict to Following Types:</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="tw-align-top tw-pr-2">
                    <label class="tw-sr-only" for="qp-time-benefit-types-avail"
                      >Available benefit types</label
                    >
                    <select
                      id="qp-time-benefit-types-avail"
                      #availTimeTypes
                      multiple
                      class="tw-w-full tw-min-w-[220px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    >
                      @for (o of timeTypeAvailableDualList; track o.value + '|' + o.label) {
                        <option [value]="o.value">{{ o.label }}</option>
                      }
                    </select>
                  </td>
                  <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                      <app-button
                        label="→"
                        ariaLabel="Add selected benefit types"
                        (click)="addSelectedTimeTypes(availTimeTypes)"
                      />
                      <app-button label="⇒" ariaLabel="Add all benefit types" (click)="addAllTimeTypes()" />
                      <app-button
                        label="←"
                        ariaLabel="Remove selected benefit types"
                        (click)="removeSelectedTimeTypes(restTimeTypes)"
                      />
                      <app-button label="⇐" ariaLabel="Remove all benefit types" (click)="removeAllTimeTypes()" />
                    </div>
                  </td>
                  <td class="tw-align-top tw-pl-2">
                    <label class="tw-sr-only" for="qp-time-benefit-types-restrict"
                      >Restricted benefit types</label
                    >
                    <select
                      id="qp-time-benefit-types-restrict"
                      #restTimeTypes
                      multiple
                      class="tw-w-full tw-min-w-[220px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    >
                      @for (o of timeTypeRestrictedDualList; track o.value + '|' + o.label) {
                        <option [value]="o.value">{{ o.label }}</option>
                      }
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="tw-overflow-auto">
            <table class="tw-w-full tw-text-sm tw-border-collapse">
              <thead>
                <tr>
                  <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Available Benefit Status:</th>
                  <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                  <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Restrict to Following Status:</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="tw-align-top tw-pr-2">
                    <label class="tw-sr-only" for="qp-time-benefit-status-avail"
                      >Available benefit statuses</label
                    >
                    <select
                      id="qp-time-benefit-status-avail"
                      #availTimeStatuses
                      multiple
                      class="tw-w-full tw-min-w-[220px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    >
                      @for (o of timeStatusAvailableDualList; track o.value + '|' + o.label) {
                        <option [value]="o.value">{{ o.label }}</option>
                      }
                    </select>
                  </td>
                  <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                      <app-button
                        label="→"
                        ariaLabel="Add selected benefit statuses"
                        (click)="addSelectedTimeStatuses(availTimeStatuses)"
                      />
                      <app-button label="⇒" ariaLabel="Add all benefit statuses" (click)="addAllTimeStatuses()" />
                      <app-button
                        label="←"
                        ariaLabel="Remove selected benefit statuses"
                        (click)="removeSelectedTimeStatuses(restTimeStatuses)"
                      />
                      <app-button label="⇐" ariaLabel="Remove all benefit statuses" (click)="removeAllTimeStatuses()" />
                    </div>
                  </td>
                  <td class="tw-align-top tw-pl-2">
                    <label class="tw-sr-only" for="qp-time-benefit-status-restrict"
                      >Restricted benefit statuses</label
                    >
                    <select
                      id="qp-time-benefit-status-restrict"
                      #restTimeStatuses
                      multiple
                      class="tw-w-full tw-min-w-[220px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    >
                      @for (o of timeStatusRestrictedDualList; track o.value) {
                        <option [value]="o.value">{{ o.label }}</option>
                      }
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="tw-overflow-auto">
            <table class="tw-w-full tw-text-sm tw-border-collapse">
              <thead>
                <tr>
                  <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Available Benefit Reasons:</th>
                  <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                  <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Restrict to Following Reasons:</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="tw-align-top tw-pr-2">
                    <label class="tw-sr-only" for="qp-time-benefit-reasons-avail"
                      >Available benefit reasons</label
                    >
                    <select
                      id="qp-time-benefit-reasons-avail"
                      #availTimeReasons
                      multiple
                      class="tw-w-full tw-min-w-[220px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    >
                      @for (o of timeReasonAvailableDualList; track o.value + '|' + o.label) {
                        <option [value]="o.value">{{ o.label }}</option>
                      }
                    </select>
                  </td>
                  <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                    <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                      <app-button
                        label="→"
                        ariaLabel="Add selected benefit reasons"
                        (click)="addSelectedTimeReasons(availTimeReasons)"
                      />
                      <app-button label="⇒" ariaLabel="Add all benefit reasons" (click)="addAllTimeReasons()" />
                      <app-button
                        label="←"
                        ariaLabel="Remove selected benefit reasons"
                        (click)="removeSelectedTimeReasons(restTimeReasons)"
                      />
                      <app-button label="⇐" ariaLabel="Remove all benefit reasons" (click)="removeAllTimeReasons()" />
                    </div>
                  </td>
                  <td class="tw-align-top tw-pl-2">
                    <label class="tw-sr-only" for="qp-time-benefit-reasons-restrict"
                      >Restricted benefit reasons</label
                    >
                    <select
                      id="qp-time-benefit-reasons-restrict"
                      #restTimeReasons
                      multiple
                      class="tw-w-full tw-min-w-[220px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    >
                      @for (o of timeReasonRestrictedDualList; track o.value + '|' + o.label) {
                        <option [value]="o.value">{{ o.label }}</option>
                      }
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </ng-template>

      @switch (panelKey) {
        @case ('ClaimType') {
          <div class="tw-max-w-[960px] tw-space-y-6">
            @if (wcClaimTypeSectionVisible) {
              <ng-container *ngTemplateOutlet="wcClaimTypeTpl" />
            }
            @if (dsClaimTypeSectionVisible) {
              <app-query-disability-lob
                [extended]="extended"
                [queryForm]="queryForm"
                [queryUniqueKey]="queryUniqueKey"
                [claimTypesOnly]="true"
              />
            }
            @if (!wcClaimTypeSectionVisible && !dsClaimTypeSectionVisible) {
              <h3 class="tw-font-semibold tw-text-lg tw-pb-2">Claim Type</h3>
              <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">
                Select Workers' Comp or Disability in Lines of Business under General to set claim types.
              </p>
            }
          </div>
        }
        @case ('States') {
          <div class="tw-max-w-[900px] tw-space-y-4">
            <h3 class="tw-font-semibold tw-text-lg">States</h3>
            <ng-container *ngTemplateOutlet="claimStatesDualListsTpl" />
          </div>
        }
        @case ('ClaimFlags') {
          <ng-container *ngTemplateOutlet="claimFlagsPanelTpl" />
        }
        @case ('AdvClaimFlags') {
          <ng-container *ngTemplateOutlet="claimFlagsPanelTpl" />
        }
        @case ('Examiner') {
          <div class="tw-max-w-[900px] tw-space-y-4">
            <h3 class="tw-font-semibold tw-text-lg">Examiner</h3>
            @if (examinersLoading) {
              <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading…</p>
            } @else {
              <div class="tw-overflow-auto">
                <table class="tw-w-full tw-text-sm tw-border-collapse">
                  <thead>
                    <tr>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Available Examiner Offices</th>
                      <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove offices between lists</span>
                      </th>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Restrict to Following Offices</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="tw-align-top tw-pr-2">
                        <label class="tw-sr-only" for="qp-examiner-avail-offices"
                          >Available examiner offices</label
                        >
                        <select
                          id="qp-examiner-avail-offices"
                          #availExamOffices
                          multiple
                          class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        >
                          @for (o of availableExaminerOfficeOptions; track o.value) {
                            <option [value]="o.value">{{ o.label }}</option>
                          }
                        </select>
                      </td>
                      <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                        <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                          <app-button
                            label="→"
                            ariaLabel="Add selected offices"
                            (click)="addSelectedExaminerOffices(availExamOffices)"
                          />
                          <app-button
                            label="⇒"
                            ariaLabel="Add all offices"
                            (click)="addAllExaminerOffices()"
                          />
                          <app-button
                            label="←"
                            ariaLabel="Remove selected offices"
                            (click)="removeSelectedExaminerOffices(restExamOffices)"
                          />
                          <app-button
                            label="⇐"
                            ariaLabel="Remove all offices"
                            (click)="removeAllExaminerOffices()"
                          />
                        </div>
                      </td>
                      <td class="tw-align-top tw-pl-2">
                        <label class="tw-sr-only" for="qp-examiner-restrict-offices"
                          >Restricted examiner offices</label
                        >
                        <select
                          id="qp-examiner-restrict-offices"
                          #restExamOffices
                          multiple
                          class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        >
                          @for (o of restrictedExaminerOfficeOptions; track o.value) {
                            <option [value]="o.value">{{ o.label }}</option>
                          }
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pt-4 tw-pb-2 tw-pr-2">Enter Examiner ID</th>
                      <th scope="col" class="tw-w-14 tw-pt-4 tw-pb-2">
                        <span class="tw-sr-only">Add or remove examiners between lists</span>
                      </th>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pt-4 tw-pb-2 tw-pl-2">Restrict to Following Examiners</th>
                    </tr>
                    <tr>
                      <td class="tw-align-top tw-pr-2 tw-pt-1">
                        <app-input
                          [control]="examinerIdDraft"
                          label=""
                          placeholder="Examiner ID"
                          screenReaderLabel="Examiner ID"
                        />
                      </td>
                      <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                        <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                          <app-button label="→" ariaLabel="Add examiner ID" (click)="addExaminerIdFromDraft()" />
                          <app-button
                            label="←"
                            ariaLabel="Remove selected examiners"
                            (click)="removeSelectedExaminerIds(restExaminerIds)"
                          />
                        </div>
                      </td>
                      <td class="tw-align-top tw-pl-2 tw-pt-1">
                        <label class="tw-sr-only" for="qp-examiner-restrict-ids"
                          >Restricted examiner IDs</label
                        >
                        <select
                          id="qp-examiner-restrict-ids"
                          #restExaminerIds
                          multiple
                          class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        >
                          @for (id of examinerIdsArray; track id) {
                            <option [value]="id">{{ id }}</option>
                          }
                        </select>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            }
          </div>
        }
        @case ('ExaminersOffices') {
          <div class="tw-max-w-[900px] tw-space-y-4">
            <h3 class="tw-font-semibold tw-text-lg">Examiners / Offices</h3>
            @if (examinersLoading) {
              <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading…</p>
            } @else {
              <div class="tw-overflow-auto">
                <table class="tw-w-full tw-text-sm tw-border-collapse">
                  <thead>
                    <tr>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Enter Examiner ID</th>
                      <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Restrict to Following Examiners</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="tw-align-top tw-pr-2">
              <app-input
                          [control]="examinerIdDraft"
                          label=""
                          placeholder="Examiner ID"
                          screenReaderLabel="Examiner ID"
                        />
                      </td>
                      <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                        <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                          <app-button label="→" ariaLabel="Add examiner ID" (click)="addExaminerIdFromDraft()" />
                          <app-button label="←" ariaLabel="Remove selected examiners" (click)="removeSelectedExaminerIds(restExaminerIdsAdv)" />
                        </div>
                      </td>
                      <td class="tw-align-top tw-pl-2">
                        <label class="tw-sr-only" for="qp-exam-adv-restrict-ids"
                          >Restricted examiner IDs (advanced)</label
                        >
                        <select
                          id="qp-exam-adv-restrict-ids"
                          #restExaminerIdsAdv
                          multiple
                          class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        >
                          @for (id of examinerIdsArray; track id) {
                            <option [value]="id">{{ id }}</option>
                          }
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pt-4 tw-pb-2 tw-pr-2">Available Examiner Offices</th>
                      <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pt-4 tw-pb-2 tw-pl-2">Restrict to Following Offices</th>
                    </tr>
                    <tr>
                      <td class="tw-align-top tw-pr-2 tw-pt-1">
                        <label class="tw-sr-only" for="qp-exam-adv-avail-offices"
                          >Available examiner offices (advanced)</label
                        >
                        <select
                          id="qp-exam-adv-avail-offices"
                          #availExamAdv
                          multiple
                          class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        >
                          @for (o of availableExaminerOfficeOptions; track o.value) {
                            <option [value]="o.value">{{ o.label }}</option>
                          }
                        </select>
                      </td>
                      <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                        <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                          <app-button label="→" ariaLabel="Add selected offices" (click)="addSelectedExaminerOffices(availExamAdv)" />
                          <app-button label="⇒" ariaLabel="Add all offices" (click)="addAllExaminerOffices()" />
                          <app-button label="←" ariaLabel="Remove selected offices" (click)="removeSelectedExaminerOffices(restExamAdv)" />
                          <app-button label="⇐" ariaLabel="Remove all offices" (click)="removeAllExaminerOffices()" />
                        </div>
                      </td>
                      <td class="tw-align-top tw-pl-2 tw-pt-1">
                        <label class="tw-sr-only" for="qp-exam-adv-restrict-offices"
                          >Restricted examiner offices (advanced)</label
                        >
                        <select
                          id="qp-exam-adv-restrict-offices"
                          #restExamAdv
                          multiple
                          class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        >
                          @for (o of restrictedExaminerOfficeOptions; track o.value) {
                            <option [value]="o.value">{{ o.label }}</option>
                          }
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pt-4 tw-pb-2 tw-pr-2">Available Processing Offices</th>
                      <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pt-4 tw-pb-2 tw-pl-2">Restrict to Following Offices</th>
                    </tr>
                    <tr>
                      <td class="tw-align-top tw-pr-2 tw-pt-1">
                        <label class="tw-sr-only" for="qp-exam-adv-avail-processing"
                          >Available processing offices</label
                        >
                        <select
                          id="qp-exam-adv-avail-processing"
                          #availProcAdv
                          multiple
                          class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        >
                          @for (o of availableProcessorOfficeOptions; track o.value) {
                            <option [value]="o.value">{{ o.label }}</option>
                          }
                        </select>
                      </td>
                      <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                        <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                          <app-button label="→" ariaLabel="Add selected processing offices" (click)="addSelectedProcessorOffices(availProcAdv)" />
                          <app-button label="⇒" ariaLabel="Add all processing offices" (click)="addAllProcessorOffices()" />
                          <app-button label="←" ariaLabel="Remove selected processing offices" (click)="removeSelectedProcessorOffices(restProcAdv)" />
                          <app-button label="⇐" ariaLabel="Remove all processing offices" (click)="removeAllProcessorOffices()" />
                        </div>
                      </td>
                      <td class="tw-align-top tw-pl-2 tw-pt-1">
                        <label class="tw-sr-only" for="qp-exam-adv-restrict-processing"
                          >Restricted processing offices</label
                        >
                        <select
                          id="qp-exam-adv-restrict-processing"
                          #restProcAdv
                          multiple
                          class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        >
                          @for (o of restrictedProcessorOfficeOptions; track o.value) {
                            <option [value]="o.value">{{ o.label }}</option>
                          }
                        </select>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            }
          </div>
        }
        @case ('ICD9') {
          <div class="tw-max-w-[900px] tw-space-y-4">
            <h3 class="tw-font-semibold tw-text-lg">ICD Codes</h3>
            <div class="tw-overflow-auto">
              <table class="tw-w-full tw-text-sm tw-border-collapse">
                <thead>
                  <tr>
                    <th scope="col" class="tw-sr-only">Search criterion</th>
                    <th scope="col" class="tw-sr-only">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th
                      scope="row"
                      id="qp-icd9-menu-version-label"
                      class="tw-py-1 tw-pr-4 tw-align-top tw-font-medium tw-whitespace-nowrap tw-text-left tw-font-normal"
                    >
                      ICD version
                    </th>
                    <td class="tw-py-1">
                      <select
                        aria-labelledby="qp-icd9-menu-version-label"
                        [formControl]="icdVersionSelect"
                        class="tw-min-w-[140px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                      >
                        <option value="">All ICDs</option>
                        <option value="10">ICD10</option>
                        <option value="9">ICD9</option>
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" class="tw-py-1 tw-pr-4 tw-align-top tw-font-medium tw-whitespace-nowrap tw-text-left tw-font-normal">Code begins</th>
                    <td class="tw-py-1">
                      <div class="tw-flex tw-flex-wrap tw-gap-2 tw-items-center">
                        <select
                          [formControl]="icdCodePrefixSelect"
                          class="tw-min-w-[88px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                          aria-label="First character of code"
                        >
                          <option value="">Any</option>
                          @for (c of icdCodePrefixChars; track c) {
                            <option [value]="c">{{ c }}</option>
                          }
                        </select>
                        <input
                          type="text"
                          [formControl]="icdCodeBeginsExtra"
                          class="tw-min-w-[160px] tw-flex-1 tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                          placeholder="Additional prefix"
                          aria-label="Additional code prefix"
                        />
                        <app-button
                          label="Search"
                          ariaLabel="Search ICD codes"
                          [loading]="icdSearchLoading"
                          (click)="runIcdSearch()"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row" class="tw-py-1 tw-pr-4 tw-align-top tw-font-medium tw-whitespace-nowrap tw-text-left tw-font-normal">
                      Description contains
                    </th>
                    <td class="tw-py-1">
                      <input
                        type="text"
                        [formControl]="icdDescriptionContains"
                        class="tw-w-full tw-max-w-md tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        aria-label="Description contains"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              <table class="tw-w-full tw-text-sm tw-border-collapse tw-mt-4">
                <thead>
                  <tr>
                    <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Search Results</th>
                    <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                    <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Restrict to Following Codes:</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="tw-align-top tw-pr-2">
                      <app-query-virtual-dual-list
                        listId="qp-icd9-search-results"
                        ariaLabel="ICD code search results"
                        [options]="icdDualLeftOptions"
                        [selection]="icdLeftSelection"
                      />
                    </td>
                    <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                      <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                        <app-button
                          label="→"
                          ariaLabel="Add selected ICD codes"
                          (click)="addSelectedIcdFromSearch()"
                        />
                        <app-button
                          label="⇒"
                          ariaLabel="Add all search results"
                          (click)="addAllIcdFromSearch()"
                        />
                        <app-button
                          label="←"
                          ariaLabel="Remove selected ICD codes"
                          (click)="removeSelectedIcd()"
                        />
                        <app-button
                          label="⇐"
                          ariaLabel="Remove all ICD codes"
                          (click)="removeAllIcd()"
                        />
                      </div>
                    </td>
                    <td class="tw-align-top tw-pl-2">
                      <app-query-virtual-dual-list
                        listId="qp-icd9-restricted"
                        ariaLabel="Restricted ICD codes"
                        [options]="icdDualRightOptions"
                        [selection]="icdRightSelection"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        }
        @case ('PaymentsBanks') {
          <div class="tw-max-w-[960px] tw-space-y-6">
            <h3 class="tw-font-semibold tw-text-lg">Payments/Banks</h3>
            <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
              <div>
                <h3 class="tw-font-semibold tw-text-lg tw-pb-2">Include These Payment Types:</h3>
                @if (paymentsLoading) {
                  <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading…</p>
                } @else {
                  <div class="tw-flex tw-flex-col tw-gap-2">
                    @for (p of paymentRowsPaymentTypesOnly; track p.code) {
                      <app-checkbox
                        [control]="paymentCheckboxControlFor(p.code)"
                        [label]="p.name"
                      />
                    }
                  </div>
                }
              </div>
              <div>
                <h3 class="tw-font-semibold tw-text-lg tw-pb-2">Include These Document Types:</h3>
                @if (paymentsLoading) {
                  <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading…</p>
                } @else {
                  <div class="tw-flex tw-flex-col tw-gap-2">
                    @for (p of paymentRowsDocumentTypesOnly; track p.code) {
                      <app-checkbox
                        [control]="paymentCheckboxControlFor(p.code)"
                        [label]="p.name"
                      />
                    }
                  </div>
                }
              </div>
              <div>
                <h3 class="tw-font-semibold tw-text-lg tw-pb-2">Pay code series</h3>
                <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2">
                  @for (s of payCodeSeriesDefs; track s.digit) {
                    <app-checkbox
                      [control]="paySeriesCheckboxControl(s.digit)"
                      [label]="s.label"
                    />
                  }
                </div>
              </div>
            </div>
            <div class="tw-space-y-4 tw-pt-2 tw-border-t tw-border-[var(--sed-black-60-color)]">
              <h3 class="tw-font-semibold tw-text-lg">Pay codes</h3>
              <div class="tw-overflow-auto">
                <table class="tw-w-full tw-text-sm tw-border-collapse">
                  <thead>
                    <tr>
                      <th scope="col" class="tw-sr-only">Criterion</th>
                      <th scope="col" class="tw-sr-only">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th scope="row" class="tw-py-1 tw-pr-4 tw-align-top tw-font-medium tw-whitespace-nowrap tw-text-left tw-font-normal">
                        Pay code begins
                      </th>
                      <td class="tw-py-1">
                        <input
                          type="text"
                          [formControl]="paycodeSearchPrefix"
                          class="tw-w-full tw-max-w-xs tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                          aria-label="Pay code begins"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th scope="row" class="tw-py-1 tw-pr-4 tw-align-top tw-font-medium tw-whitespace-nowrap tw-text-left tw-font-normal">
                        Description contains
                      </th>
                      <td class="tw-py-1">
                        <div class="tw-flex tw-flex-wrap tw-gap-2 tw-items-center">
                          <input
                            type="text"
                            [formControl]="paycodeSearchDescription"
                            class="tw-min-w-[200px] tw-flex-1 tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                            aria-label="Description contains"
                          />
                          <app-button
                            label="Search"
                            ariaLabel="Search pay codes"
                            [loading]="paycodesLoading"
                            (click)="runPaycodeSearch()"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table class="tw-w-full tw-text-sm tw-border-collapse tw-mt-4">
                  <thead>
                    <tr>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Search Results</th>
                      <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Restrict to Following Pay Codes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="tw-align-top tw-pr-2">
                        <label class="tw-sr-only" for="qp-paycodes-search-avail"
                          >Pay code search results</label
                        >
                        <select
                          id="qp-paycodes-search-avail"
                          #availPaycodeSearch
                          multiple
                          class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        >
                          @for (o of availablePaycodeSearchDualList; track o.value + '|' + o.label) {
                            <option [value]="o.value">{{ o.label }}</option>
                          }
                        </select>
                      </td>
                      <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                        <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                          <app-button
                            label="→"
                            ariaLabel="Add selected pay codes"
                            (click)="addSelectedPaycodesFromSearch(availPaycodeSearch)"
                          />
                          <app-button
                            label="⇒"
                            ariaLabel="Add all search results"
                            (click)="addAllPaycodesFromSearch()"
                          />
                          <app-button
                            label="←"
                            ariaLabel="Remove selected pay codes"
                            (click)="removeSelectedPaycodesFromRestricted(restPaycodeSearch)"
                          />
                          <app-button
                            label="⇐"
                            ariaLabel="Remove all pay codes"
                            (click)="removeAllPaycodesFromRestricted()"
                          />
                        </div>
                      </td>
                      <td class="tw-align-top tw-pl-2">
                        <label class="tw-sr-only" for="qp-paycodes-search-restrict"
                          >Restricted pay codes</label
                        >
                        <select
                          id="qp-paycodes-search-restrict"
                          #restPaycodeSearch
                          multiple
                          class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        >
                          @for (o of restrictedPaycodeDualList; track o.value + '|' + o.label) {
                            <option [value]="o.value">{{ o.label }}</option>
                          }
                        </select>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="tw-pt-4 tw-border-t tw-border-[var(--sed-black-60-color)]">
              <h3 class="tw-font-semibold tw-text-lg tw-pb-2">Bank accounts</h3>
              @if (bankingLoading) {
                <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading…</p>
              } @else {
                <div class="tw-overflow-auto">
                  <table class="tw-w-full tw-text-sm tw-border-collapse">
                    <thead>
                      <tr>
                        <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Available Accounts</th>
                        <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                        <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Restrict to Following Accounts</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td class="tw-align-top tw-pr-2">
                          <label class="tw-sr-only" for="qp-banks-payments-avail"
                            >Available bank accounts</label
                          >
                          <select
                            id="qp-banks-payments-avail"
                            #availBanksPb
                            multiple
                            class="tw-w-full tw-min-w-[240px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                          >
                            @for (o of availableBankingOptionsForDualList; track o.value) {
                              <option [value]="o.value">{{ o.label }}</option>
                            }
                          </select>
                        </td>
                        <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                          <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                            <app-button label="→" ariaLabel="Add selected banks" (click)="addSelectedBanks(availBanksPb)" />
                            <app-button label="⇒" ariaLabel="Add all banks" (click)="addAllBanks()" />
                            <app-button label="←" ariaLabel="Remove selected banks" (click)="removeSelectedBanks(restBanksPb)" />
                            <app-button label="⇐" ariaLabel="Remove all banks" (click)="removeAllBanks()" />
                          </div>
                        </td>
                        <td class="tw-align-top tw-pl-2">
                          <label class="tw-sr-only" for="qp-banks-payments-restrict"
                            >Restricted bank accounts</label
                          >
                          <select
                            id="qp-banks-payments-restrict"
                            #restBanksPb
                            multiple
                            class="tw-w-full tw-min-w-[240px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                          >
                            @for (o of restrictedBankingOptionsForDualList; track o.value) {
                              <option [value]="o.value">{{ o.label }}</option>
                            }
                          </select>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              }
            </div>
          </div>
        }
        @case ('BankAccounts') {
          <div class="tw-max-w-[900px] tw-space-y-4">
            <h3 class="tw-font-semibold tw-text-lg">Bank Accounts</h3>
            @if (bankingLoading) {
              <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading…</p>
            } @else {
              <div class="tw-overflow-auto">
                <table class="tw-w-full tw-text-sm tw-border-collapse">
                  <thead>
                    <tr>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Available Accounts</th>
                      <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                      <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Restrict to Following Accounts</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="tw-align-top tw-pr-2">
                        <label class="tw-sr-only" for="qp-banks-accounts-avail"
                          >Available bank accounts</label
                        >
                        <select
                          id="qp-banks-accounts-avail"
                          #availBanks
                          multiple
                          class="tw-w-full tw-min-w-[240px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        >
                          @for (o of availableBankingOptionsForDualList; track o.value) {
                            <option [value]="o.value">{{ o.label }}</option>
                          }
                        </select>
                      </td>
                      <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                        <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                          <app-button label="→" ariaLabel="Add selected banks" (click)="addSelectedBanks(availBanks)" />
                          <app-button label="⇒" ariaLabel="Add all banks" (click)="addAllBanks()" />
                          <app-button label="←" ariaLabel="Remove selected banks" (click)="removeSelectedBanks(restBanks)" />
                          <app-button label="⇐" ariaLabel="Remove all banks" (click)="removeAllBanks()" />
                        </div>
                      </td>
                      <td class="tw-align-top tw-pl-2">
                        <label class="tw-sr-only" for="qp-banks-accounts-restrict"
                          >Restricted bank accounts</label
                        >
                        <select
                          id="qp-banks-accounts-restrict"
                          #restBanks
                          multiple
                          class="tw-w-full tw-min-w-[240px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                        >
                          @for (o of restrictedBankingOptionsForDualList; track o.value) {
                            <option [value]="o.value">{{ o.label }}</option>
                          }
                        </select>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            }
          </div>
        }
        @case ('Payments') {
          <div class="tw-max-w-[960px] tw-space-y-4">
            <h3 class="tw-font-semibold tw-text-lg tw-pb-1">Payments</h3>
            <div
              class="tw-flex tw-flex-wrap tw-gap-1 tw-border-b tw-border-[var(--sed-black-60-color)]"
              role="tablist"
              aria-label="Advanced Payments"
            >
              @for (t of advPaymentsTabs; track t.id) {
                <button
                  type="button"
                  role="tab"
                  [attr.aria-selected]="advPaymentsSubTab === t.id"
                  class="tw-px-3 tw-py-2 tw-text-sm tw-rounded-t tw-border tw-mb-[-1px] tw-transition-colors"
                  [class.tw-border-[var(--sed-black-60-color)]]="advPaymentsSubTab === t.id"
                  [class.tw-border-b-white]="advPaymentsSubTab === t.id"
                  [class.tw-bg-white]="advPaymentsSubTab === t.id"
                  [class.tw-font-semibold]="advPaymentsSubTab === t.id"
                  [class.tw-border-transparent]="advPaymentsSubTab !== t.id"
                  [class.tw-text-[var(--sed-black-60-color)]]="advPaymentsSubTab !== t.id"
                  (click)="onAdvPaymentsSubTabSelect(t.id)"
                >
                  {{ t.label }}
                </button>
              }
            </div>
            @if (paymentsLoading && advPaymentsSubTab === 'general') {
              <p class="tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2">Loading…</p>
            }
            @if (!paymentsLoading && advPaymentsSubTab === 'general') {
              <div class="tw-pt-3 tw-space-y-6" [formGroup]="advancedPaymentsGroup">
                <table class="tw-w-full tw-text-sm tw-border-collapse tw-max-w-[640px]">
                  <thead>
                    <tr>
                      <th scope="col" class="tw-sr-only">Financial measure</th>
                      <th scope="col" class="tw-pb-2 tw-text-left tw-font-semibold">From</th>
                      <th scope="col" class="tw-pb-2 tw-text-left tw-font-semibold">Through</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th scope="row" class="tw-py-2 tw-pr-4 tw-align-top tw-text-left tw-font-medium">Allocation amount</th>
                      <td class="tw-py-2 tw-pr-2 tw-align-top">
                        <input
                          type="text"
                          formControlName="allocationFrom"
                          class="tw-w-full tw-max-w-[140px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                          aria-label="Allocation amount from"
                        />
                        <app-help-text
                          [control]="$any(advancedPaymentsGroup.get('allocationFrom'))"
                          label="Allocation amount From"
                          [showMaxlength]="false"
                        />
                      </td>
                      <td class="tw-py-2 tw-align-top">
                        <input
                          type="text"
                          formControlName="allocationThrough"
                          class="tw-w-full tw-max-w-[140px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                          aria-label="Allocation amount through"
                        />
                        <app-help-text
                          [control]="$any(advancedPaymentsGroup.get('allocationThrough'))"
                          label="Allocation amount Through"
                          [showMaxlength]="false"
                        />
                      </td>
                    </tr>
                    <tr>
                      <th scope="row" class="tw-py-2 tw-pr-4 tw-align-top tw-text-left tw-font-medium">Document amount</th>
                      <td class="tw-py-2 tw-pr-2 tw-align-top">
                        <input
                          type="text"
                          formControlName="documentFrom"
                          class="tw-w-full tw-max-w-[140px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                          aria-label="Document amount from"
                        />
                        <app-help-text
                          [control]="$any(advancedPaymentsGroup.get('documentFrom'))"
                          label="Document amount From"
                          [showMaxlength]="false"
                        />
                      </td>
                      <td class="tw-py-2 tw-align-top">
                        <input
                          type="text"
                          formControlName="documentThrough"
                          class="tw-w-full tw-max-w-[140px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                          aria-label="Document amount through"
                        />
                        <app-help-text
                          [control]="$any(advancedPaymentsGroup.get('documentThrough'))"
                          label="Document amount Through"
                          [showMaxlength]="false"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
                  <div>
                    <h4 class="tw-font-semibold tw-text-base tw-pb-2">Include These Payment Types:</h4>
                    <div class="tw-flex tw-flex-col tw-gap-2">
                      @for (p of paymentRowsPaymentTypesOnly; track p.code) {
                        <app-checkbox
                          [control]="paymentCheckboxControlFor(p.code)"
                          [label]="p.name"
                        />
                      }
                    </div>
                  </div>
                  <div>
                    <h4 class="tw-font-semibold tw-text-base tw-pb-2">Include These Document Types:</h4>
                    <div class="tw-flex tw-flex-col tw-gap-2">
                      @for (p of paymentRowsDocumentTypesOnly; track p.code) {
                        <app-checkbox
                          [control]="paymentCheckboxControlFor(p.code)"
                          [label]="p.name"
                        />
                      }
                    </div>
                  </div>
                </div>

                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6 tw-pt-2 tw-border-t tw-border-[var(--sed-black-60-color)]">
                  <div class="tw-space-y-3">
                    <app-radio-button
                      [control]="$any(advancedPaymentsGroup.get('clearedStatus'))"
                      [options]="advPaymentsClearedStatusOptions"
                      optionLabel="label"
                      optionValue="value"
                      label="Cleared status"
                      [showClear]="false"
                      [vertical]="true"
                    />
                    <div class="tw-space-y-2">
                      <div class="tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)]">Payment date</div>
                      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                        <app-date-picker
                          [control]="$any(advancedPaymentsGroup.get('clearedPaymentFrom'))"
                          label="Payment from"
                        />
                        <app-date-picker
                          [control]="$any(advancedPaymentsGroup.get('clearedPaymentTo'))"
                          label="Payment to"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="tw-space-y-3">
                    <app-radio-button
                      [control]="$any(advancedPaymentsGroup.get('escheatedStatus'))"
                      [options]="advPaymentsEscheatedStatusOptions"
                      optionLabel="label"
                      optionValue="value"
                      label="Escheated status"
                      [showClear]="false"
                      [vertical]="true"
                    />
                    <div class="tw-space-y-2">
                      <div class="tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)]">Escheated date</div>
                      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                        <app-date-picker
                          [control]="$any(advancedPaymentsGroup.get('escheatedFrom'))"
                          label="Escheated from"
                        />
                        <app-date-picker
                          [control]="$any(advancedPaymentsGroup.get('escheatedTo'))"
                          label="Escheated to"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
            @if (advPaymentsSubTab === 'payCodes') {
              <div class="tw-pt-3 tw-space-y-4">
                <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6 tw-items-start">
                  <div class="tw-space-y-3">
                    <table class="tw-w-full tw-text-sm tw-border-collapse">
                      <thead>
                        <tr>
                          <th scope="col" class="tw-sr-only">Criterion</th>
                          <th scope="col" class="tw-sr-only">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <th scope="row" class="tw-py-1 tw-pr-4 tw-align-top tw-font-medium tw-whitespace-nowrap tw-text-left tw-font-normal">
                            Pay code begins
                          </th>
                          <td class="tw-py-1">
                            <input
                              type="text"
                              [formControl]="paycodeSearchPrefix"
                              class="tw-w-full tw-max-w-xs tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                              aria-label="Pay code begins"
                            />
                          </td>
                        </tr>
                        <tr>
                          <th scope="row" class="tw-py-1 tw-pr-4 tw-align-top tw-font-medium tw-whitespace-nowrap tw-text-left tw-font-normal">
                            Description contains
                          </th>
                          <td class="tw-py-1">
                            <div class="tw-flex tw-flex-wrap tw-gap-2 tw-items-center">
                              <input
                                type="text"
                                [formControl]="paycodeSearchDescription"
                                class="tw-min-w-[200px] tw-flex-1 tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                                aria-label="Description contains"
                              />
                              <app-button
                                label="Search"
                                ariaLabel="Search pay codes"
                                [loading]="paycodesLoading"
                                (click)="runPaycodeSearch()"
                              />
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div>
                    <h4 class="tw-font-semibold tw-text-base tw-pb-2">Pay code series</h4>
                    <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2">
                      @for (s of payCodeSeriesDefs; track s.digit) {
                        <app-checkbox
                          [control]="paySeriesCheckboxControl(s.digit)"
                          [label]="s.label"
                        />
                      }
                    </div>
                  </div>
                </div>
                @if (paycodesLoading) {
                  <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading pay codes…</p>
                } @else {
                  <div class="tw-overflow-auto">
                    <table class="tw-w-full tw-text-sm tw-border-collapse">
                      <thead>
                        <tr>
                          <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Search Results</th>
                          <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                          <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Restrict to Following Pay Codes</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td class="tw-align-top tw-pr-2">
                            <label class="tw-sr-only" for="qp-paycodes-adv-avail"
                              >Pay code search results (advanced payments)</label
                            >
                            <select
                              id="qp-paycodes-adv-avail"
                              #availPaycodeAdv
                              multiple
                              class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                            >
                              @for (o of availablePaycodeSearchDualList; track o.value + '|' + o.label) {
                                <option [value]="o.value">{{ o.label }}</option>
                              }
                            </select>
                          </td>
                          <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                            <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                              <app-button
                                label="→"
                                ariaLabel="Add selected pay codes"
                                (click)="addSelectedPaycodesFromSearch(availPaycodeAdv)"
                              />
                              <app-button
                                label="⇒"
                                ariaLabel="Add all search results"
                                (click)="addAllPaycodesFromSearch()"
                              />
                              <app-button
                                label="←"
                                ariaLabel="Remove selected pay codes"
                                (click)="removeSelectedPaycodesFromRestricted(restPaycodeAdv)"
                              />
                              <app-button
                                label="⇐"
                                ariaLabel="Remove all pay codes"
                                (click)="removeAllPaycodesFromRestricted()"
                              />
                            </div>
                          </td>
                          <td class="tw-align-top tw-pl-2">
                            <label class="tw-sr-only" for="qp-paycodes-adv-restrict"
                              >Restricted pay codes (advanced payments)</label
                            >
                            <select
                              id="qp-paycodes-adv-restrict"
                              #restPaycodeAdv
                              multiple
                              class="tw-w-full tw-min-w-[200px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                            >
                              @for (o of restrictedPaycodeDualList; track o.value + '|' + o.label) {
                                <option [value]="o.value">{{ o.label }}</option>
                              }
                            </select>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                }
              </div>
            }
          </div>
        }
        @case ('TimeTracking') {
          <div class="tw-max-w-[960px] tw-space-y-4">
            <h3 class="tw-font-semibold tw-text-lg">Time Tracking</h3>
            @if (timeLoading) {
              <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading…</p>
            } @else {
              <ng-container *ngTemplateOutlet="timeBenefitDualLists" />
            }
          </div>
        }
        @case ('AdvTimeTracking') {
          <div class="tw-max-w-[960px] tw-space-y-4">
            <h3 class="tw-font-semibold tw-text-lg">Time Tracking</h3>
            @if (timeLoading) {
              <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading…</p>
            } @else {
              <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-pb-2">
                <app-date-picker [control]="$any(timeGroup.controls['benefitBegin'])" label="Benefit from" />
                <app-date-picker [control]="$any(timeGroup.controls['benefitEnd'])" label="Benefit to" />
              </div>
              <ng-container *ngTemplateOutlet="timeBenefitDualLists" />
            }
          </div>
        }
        @case ('Financials') {
          <div class="tw-max-w-[960px] tw-space-y-6">
            <h3 class="tw-font-semibold tw-text-lg">Financials</h3>
            @if (financialLoading) {
              <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading…</p>
            } @else {
              <div class="tw-space-y-4">
                <h4 class="tw-font-semibold tw-text-base">Claim financials</h4>
                <div class="tw-space-y-3">
                  <div
                    [formGroup]="$any(financialsFormGroup.get('claimFutureReserve'))"
                    class="tw-grid tw-grid-cols-1 md:tw-grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,1fr))] tw-gap-3 tw-items-end"
                  >
                    <span class="tw-font-medium tw-text-sm tw-pb-2 md:tw-pb-0">Future reserve</span>
                    <app-checkbox [control]="$any(financialsFormGroup.get('claimFutureReserve')?.get('excludeZero'))" label="Exclude zero-valued claims" />
                    <app-input [control]="$any(financialsFormGroup.get('claimFutureReserve')?.get('from'))" label="From" type="Decimal" />
                    <app-input [control]="$any(financialsFormGroup.get('claimFutureReserve')?.get('through'))" label="Through" type="Decimal" />
                  </div>
                  <div
                    [formGroup]="$any(financialsFormGroup.get('claimIncurred'))"
                    class="tw-grid tw-grid-cols-1 md:tw-grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,1fr))] tw-gap-3 tw-items-end"
                  >
                    <span class="tw-font-medium tw-text-sm tw-pb-2 md:tw-pb-0">Incurred</span>
                    <app-checkbox [control]="$any(financialsFormGroup.get('claimIncurred')?.get('excludeZero'))" label="Exclude zero-valued claims" />
                    <app-input [control]="$any(financialsFormGroup.get('claimIncurred')?.get('from'))" label="From" type="Decimal" />
                    <app-input [control]="$any(financialsFormGroup.get('claimIncurred')?.get('through'))" label="Through" type="Decimal" />
                  </div>
                  <div
                    [formGroup]="$any(financialsFormGroup.get('claimPaid'))"
                    class="tw-grid tw-grid-cols-1 md:tw-grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,1fr))] tw-gap-3 tw-items-end"
                  >
                    <span class="tw-font-medium tw-text-sm tw-pb-2 md:tw-pb-0">Paid</span>
                    <app-checkbox [control]="$any(financialsFormGroup.get('claimPaid')?.get('excludeZero'))" label="Exclude zero-valued claims" />
                    <app-input [control]="$any(financialsFormGroup.get('claimPaid')?.get('from'))" label="From" type="Decimal" />
                    <app-input [control]="$any(financialsFormGroup.get('claimPaid')?.get('through'))" label="Through" type="Decimal" />
                  </div>
                </div>
              </div>
              <div class="tw-space-y-4 tw-pt-4 tw-border-t tw-border-[var(--sed-black-60-color)]">
                <h4 class="tw-font-semibold tw-text-base">Event financials</h4>
                <div class="tw-space-y-3">
                  <div
                    [formGroup]="$any(financialsFormGroup.get('eventFutureReserve'))"
                    class="tw-grid tw-grid-cols-1 md:tw-grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,1fr))] tw-gap-3 tw-items-end"
                  >
                    <span class="tw-font-medium tw-text-sm tw-pb-2 md:tw-pb-0">Future reserve</span>
                    <app-checkbox [control]="$any(financialsFormGroup.get('eventFutureReserve')?.get('excludeZero'))" label="Exclude zero-valued claims" />
                    <app-input [control]="$any(financialsFormGroup.get('eventFutureReserve')?.get('from'))" label="From" type="Decimal" />
                    <app-input [control]="$any(financialsFormGroup.get('eventFutureReserve')?.get('through'))" label="Through" type="Decimal" />
                  </div>
                  <div
                    [formGroup]="$any(financialsFormGroup.get('eventIncurred'))"
                    class="tw-grid tw-grid-cols-1 md:tw-grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,1fr))] tw-gap-3 tw-items-end"
                  >
                    <span class="tw-font-medium tw-text-sm tw-pb-2 md:tw-pb-0">Incurred</span>
                    <app-checkbox [control]="$any(financialsFormGroup.get('eventIncurred')?.get('excludeZero'))" label="Exclude zero-valued claims" />
                    <app-input [control]="$any(financialsFormGroup.get('eventIncurred')?.get('from'))" label="From" type="Decimal" />
                    <app-input [control]="$any(financialsFormGroup.get('eventIncurred')?.get('through'))" label="Through" type="Decimal" />
                  </div>
                  <div
                    [formGroup]="$any(financialsFormGroup.get('eventPaid'))"
                    class="tw-grid tw-grid-cols-1 md:tw-grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,1fr))] tw-gap-3 tw-items-end"
                  >
                    <span class="tw-font-medium tw-text-sm tw-pb-2 md:tw-pb-0">Paid</span>
                    <app-checkbox [control]="$any(financialsFormGroup.get('eventPaid')?.get('excludeZero'))" label="Exclude zero-valued claims" />
                    <app-input [control]="$any(financialsFormGroup.get('eventPaid')?.get('from'))" label="From" type="Decimal" />
                    <app-input [control]="$any(financialsFormGroup.get('eventPaid')?.get('through'))" label="Through" type="Decimal" />
                  </div>
                </div>
              </div>
            }
          </div>
        }
        @case ('Locations') {
          <div class="tw-max-w-[960px] tw-space-y-4">
            <h3 class="tw-font-semibold tw-text-lg">Locations</h3>
            @if (locationsLoading) {
              <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading…</p>
            } @else {
              <app-select
                [control]="locationSelectionMethod"
                [options]="locationMethodOptions"
                optionLabel="label"
                optionValue="value"
                label="Method For Selecting Locations"
                [showClear]="false"
                [filter]="false"
              />

              @if (locationSelectionMethod.value === 'Structure') {
                <div class="tw-space-y-4">
                  <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">
                    Browse the corporate structure tree, or search by location name (same data as legacy Locations wizard).
                  </p>
                  <div
                    class="tw-flex tw-flex-wrap tw-gap-3 tw-items-end tw-max-w-[720px]"
                  >
                    <div class="tw-flex-1 tw-min-w-[200px]">
                      <app-input [control]="structureSearchName" label="Name contains" />
                    </div>
                    <app-checkbox
                      [control]="structureSearchIncludeIntermediate"
                      label="Include intermediate levels"
                    />
                    <app-button
                      label="Search"
                      emphasis="Low"
                      [loading]="structureSearchLoading"
                      (click)="runStructureSearch()"
                    />
                  </div>
                  @if (structureSearchRows.length > 0) {
                    <div
                      class="tw-max-h-[220px] tw-overflow-auto tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-bg-[var(--sed-open-grey-color)]"
                    >
                      <table class="tw-w-full tw-text-sm tw-border-collapse">
                  <thead>
                          <tr class="tw-text-left tw-border-b tw-border-[var(--sed-black-60-color)]">
                            <th scope="col" class="tw-py-1 tw-pr-2 tw-font-semibold">Name</th>
                            <th scope="col" class="tw-py-1 tw-pr-2 tw-font-semibold">Code</th>
                            <th scope="col" class="tw-py-1 tw-pr-2 tw-font-semibold">Account</th>
                            <th scope="col" class="tw-py-1 tw-font-semibold">Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                          @for (r of structureSearchRows; track r.nodeId) {
                            <tr class="tw-border-b tw-border-[var(--sed-black-60-color)]/60">
                              <td class="tw-py-1 tw-pr-2">{{ r.locationName }}</td>
                              <td class="tw-py-1 tw-pr-2">{{ r.locCode }}</td>
                              <td class="tw-py-1 tw-pr-2">{{ r.accountNumber }}</td>
                              <td class="tw-py-1">{{ r.unitNumber }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
                  }
                  @if (structureTreeLoading) {
                    <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading structure…</p>
                  } @else if (structureTreeNodes.length > 0) {
                    <div class="tw-max-h-[min(420px,50vh)] tw-overflow-auto tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-bg-[var(--sed-open-grey-color)]">
                      <app-tree
                        [dataSource]="structureTreeNodes"
                        mode="checkbox"
                        (expand)="onStructureTreeExpand($event)"
                        (select)="onStructureTreeCheckboxChange($event)"
                        (unSelect)="onStructureTreeCheckboxChange($event)"
                      />
                    </div>
                  } @else if (!structureTreeLoading && structureSearchRows.length === 0) {
                    <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">
                      No corporate structure nodes were returned for this contract.
              </p>
            }
          </div>
        }

              @if (locationSelectionMethod.value === 'Accounts') {
                <div class="tw-space-y-3">
                  <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">
                    Expand an account to choose units, or use <span class="tw-font-semibold">Add all units</span> for that
                    account.
                  </p>
                  @for (acct of accountOptions; track acct.value) {
                    <div class="tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-3 tw-bg-[var(--sed-open-grey-color)]">
                      <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-pb-2">
                        <button
                          type="button"
                          class="tw-font-semibold tw-text-left tw-underline"
                          (click)="toggleLocationAccountExpand(acct.value)"
                        >
                          {{ expandedAccounts.includes(acct.value) ? '▼' : '▶' }}
                          {{ acct.label }}
                        </button>
                        <app-button
                          label="Add all units"
                          emphasis="Low"
                          [loading]="accountUnitsLoading[acct.value] === true"
                          (click)="addAllUnitsForAccount(acct.value)"
                        />
                      </div>
                      @if (expandedAccounts.includes(acct.value)) {
                        @if (accountUnitsLoading[acct.value] === true) {
                          <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading units…</p>
            } @else {
                          <app-location-account-units-panel
                            [unitOptions]="unitsByAccountCache.get(acct.value) ?? []"
                            [selectedKeys]="accountUnitIdsArray"
                            (selectionChange)="onLocationAccountUnitsSelectionChange($event)"
                          />
                        }
                      }
                    </div>
                  }
                </div>
              }

              @if (locationSelectionMethod.value === 'Units') {
                <div class="tw-space-y-4">
                  <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3 tw-max-w-[720px]">
                    <app-input [control]="locationSearchUnitNumber" label="Unit Number" />
                    <app-input [control]="locationSearchStructCode" label="Structure Code" />
                    <app-input [control]="locationSearchLocationName" label="Location Name" classes="md:tw-col-span-2" />
                    <app-input [control]="locationSearchAddress" label="Address" classes="md:tw-col-span-2" />
                    <app-input [control]="locationSearchCity" label="City" />
                    <app-input [control]="locationSearchState" label="State" />
                    <app-input [control]="locationSearchZip" label="Zip Code" />
                  </div>
                  <app-button
                    label="Search"
                    [loading]="locationSearchLoading"
                    (click)="runLocationUnitSearch()"
                  />
                  <div class="tw-overflow-auto tw-pt-2">
                    <table class="tw-w-full tw-text-sm tw-border-collapse">
                      <thead>
                        <tr>
                          <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Search Results</th>
                          <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                          <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Restrict to Selected Locations</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td class="tw-align-top tw-pr-2">
                            <label class="tw-sr-only" for="qp-location-units-search-avail"
                              >Location unit search results</label
                            >
                            <select
                              id="qp-location-units-search-avail"
                              #availLocUnits
                              multiple
                              class="tw-w-full tw-min-w-[240px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                            >
                              @for (o of availableLocationSearchOptions; track o.value) {
                                <option [value]="o.value">{{ o.label }}</option>
                              }
                            </select>
                          </td>
                          <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                            <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                              <app-button label="→" ariaLabel="Add selected" (click)="addSelectedLocationUnitsFromSearch(availLocUnits)" />
                              <app-button label="⇒" ariaLabel="Add all search results" (click)="addAllLocationUnitsFromSearch()" />
                              <app-button label="←" ariaLabel="Remove selected" (click)="removeSelectedLocationUnitsFromSearch(restLocUnits)" />
                              <app-button label="⇐" ariaLabel="Remove all" (click)="removeAllLocationUnitsFromSearch()" />
                            </div>
                          </td>
                          <td class="tw-align-top tw-pl-2">
                            <label class="tw-sr-only" for="qp-location-units-search-restrict"
                              >Restricted selected locations</label
                            >
                            <select
                              id="qp-location-units-search-restrict"
                              #restLocUnits
                              multiple
                              class="tw-w-full tw-min-w-[240px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                            >
                              @for (o of restrictedLocationUnitOptions; track o.value) {
                                <option [value]="o.value">{{ o.label }}</option>
                              }
                            </select>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              }
            }
          </div>
        }
        @case ('Claims') {
          <div class="tw-max-w-[960px] tw-space-y-4">
            <h3 class="tw-font-semibold tw-text-lg">Claims</h3>
            <div
              class="tw-flex tw-flex-wrap tw-gap-1 tw-border-b tw-border-[var(--sed-black-60-color)]"
              role="tablist"
              aria-label="Claims query options"
            >
              @for (t of claimsTabs; track t.id) {
                <button
                  type="button"
                  role="tab"
                  [attr.aria-selected]="claimsSubTab === t.id"
                  class="tw-px-3 tw-py-2 tw-text-sm tw-rounded-t tw-border tw-mb-[-1px] tw-transition-colors"
                  [class.tw-border-[var(--sed-black-60-color)]]="claimsSubTab === t.id"
                  [class.tw-border-b-white]="claimsSubTab === t.id"
                  [class.tw-bg-white]="claimsSubTab === t.id"
                  [class.tw-font-semibold]="claimsSubTab === t.id"
                  [class.tw-border-transparent]="claimsSubTab !== t.id"
                  [class.tw-text-[var(--sed-black-60-color)]]="claimsSubTab !== t.id"
                  (click)="onClaimsSubTabSelect(t.id)"
                >
                  {{ t.label }}
                </button>
              }
            </div>
            @if (claimsSubTab === 'general') {
              <div class="tw-pt-2 tw-max-w-[900px] tw-space-y-4">
                <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">
                  Lines of business and query name are set on <strong class="tw-text-[var(--sed-dynamic-black-color)]">General</strong> in the parameter
                  menu. <strong class="tw-text-[var(--sed-dynamic-black-color)]">States</strong> and <strong class="tw-text-[var(--sed-dynamic-black-color)]">Vendors</strong> tabs add geography and vendor filters.
                </p>
                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                  <app-select
                    [control]="$any(queryForm.controls['reportIncludeType'])"
                    [options]="reportIncludeTypeOptions"
                    optionLabel="label"
                    optionValue="value"
                    label="Claims/Incidents"
                  />
                  <app-date-picker [control]="$any(queryForm.controls['asOfDate'])" label="As of Date" />
                </div>
                <div>
                  <h4 class="tw-font-semibold tw-pb-2">Claim status</h4>
                  <div class="tw-flex tw-flex-wrap tw-gap-4">
                    <app-checkbox [control]="$any(queryForm.controls['claimOpen'])" label="Open" />
                    <app-checkbox [control]="$any(queryForm.controls['claimClosed'])" label="Closed" />
                    <app-checkbox [control]="$any(queryForm.controls['claimDeleted'])" label="Deleted" />
                  </div>
                </div>
                <div>
                  <h4 class="tw-font-semibold tw-pb-2">Date ranges</h4>
                  <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                    <div class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)]">Loss Date</div>
                    <app-date-picker [control]="$any(queryForm.controls['lossDateFrom'])" label="From" />
                    <app-date-picker [control]="$any(queryForm.controls['lossDateThrough'])" label="Through" />
                    <div></div>

                    <div class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2">Claim Opened</div>
                    <app-date-picker [control]="$any(queryForm.controls['openedDateFrom'])" label="From" />
                    <app-date-picker [control]="$any(queryForm.controls['openedDateThrough'])" label="Through" />
                    <div></div>

                    <div class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2">Claim Closed</div>
                    <app-date-picker [control]="$any(queryForm.controls['closedDateFrom'])" label="From" />
                    <app-date-picker [control]="$any(queryForm.controls['closedDateThrough'])" label="Through" />
                    <div></div>

                    <div class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2">Claim Reopened</div>
                    <app-date-picker [control]="$any(queryForm.controls['reopenedDateFrom'])" label="From" />
                    <app-date-picker [control]="$any(queryForm.controls['reopenedDateThrough'])" label="Through" />
                    <div></div>

                    <div class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2">Reported to Client</div>
                    <app-date-picker [control]="$any(queryForm.controls['reportedToClientFrom'])" label="From" />
                    <app-date-picker [control]="$any(queryForm.controls['reportedToClientThrough'])" label="Through" />
                    <div></div>

                    <div class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2">Reported to SCMS</div>
                    <app-date-picker [control]="$any(queryForm.controls['reportedToCmsFrom'])" label="From" screenReaderLabelPrefix="Reported to SCMS" />
                    <app-date-picker [control]="$any(queryForm.controls['reportedToCmsThrough'])" label="Through" screenReaderLabelPrefix="Reported to SCMS" />
                    <div></div>

                    <div class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2">Last Update</div>
                    <app-date-picker [control]="$any(queryForm.controls['lastUpdatedFrom'])" label="From" />
                    <app-date-picker [control]="$any(queryForm.controls['lastUpdatedThrough'])" label="Through" />
                    <div></div>

                    <div class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2">Last Financial Update</div>
                    <app-date-picker [control]="$any(queryForm.controls['lastFinancialUpdateFrom'])" label="From" />
                    <app-date-picker [control]="$any(queryForm.controls['lastFinancialUpdateThrough'])" label="Through" />
                    <div></div>

                    <div class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2">Payment Date</div>
                    <app-date-picker [control]="$any(queryForm.controls['paymentDateFrom'])" label="From" />
                    <app-date-picker [control]="$any(queryForm.controls['paymentDateThrough'])" label="Through" />
                    <div></div>
                  </div>
                </div>
              </div>
            }
            @if (claimsSubTab === 'states') {
              <div class="tw-pt-2 tw-max-w-[900px]">
                <ng-container *ngTemplateOutlet="claimStatesDualListsTpl" />
              </div>
            }
            @if (claimsSubTab === 'vendors') {
              <div class="tw-pt-2 tw-max-w-[900px]">
                @if (vendorsLoading) {
                  <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading…</p>
                } @else {
                  <div class="tw-overflow-auto">
                    <table class="tw-w-full tw-text-sm tw-border-collapse">
                      <thead>
                        <tr>
                          <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Available Vendors</th>
                          <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                          <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Selected Vendors</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td class="tw-align-top tw-pr-2">
                            <label class="tw-sr-only" for="qp-vendors-avail"
                              >Available vendors</label
                            >
                            <select
                              id="qp-vendors-avail"
                              #availVendors
                              multiple
                              class="tw-w-full tw-min-w-[240px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                            >
                              @for (o of availableVendorOptions(); track o.value) {
                                <option [value]="o.value">{{ o.label }}</option>
                              }
                            </select>
                          </td>
                          <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                            <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                              <app-button
                                label="→"
                                ariaLabel="Add selected vendors"
                                (click)="addSelectedVendors(availVendors)"
                              />
                              <app-button label="⇒" ariaLabel="Add all vendors" (click)="addAllVendors()" />
                              <app-button
                                label="←"
                                ariaLabel="Remove selected vendors"
                                (click)="removeSelectedVendors(restVendors)"
                              />
                              <app-button label="⇐" ariaLabel="Remove all vendors" (click)="removeAllVendors()" />
                            </div>
                          </td>
                          <td class="tw-align-top tw-pl-2">
                            <label class="tw-sr-only" for="qp-vendors-selected"
                              >Selected vendors</label
                            >
                            <select
                              id="qp-vendors-selected"
                              #restVendors
                              multiple
                              class="tw-w-full tw-min-w-[240px] tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                            >
                              @for (o of restrictedVendorOptions(); track o.value) {
                                <option [value]="o.value">{{ o.label }}</option>
                              }
                            </select>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                }
              </div>
            }
          </div>
        }
        @case ('MajorClass') {
          <div class="tw-max-w-[720px] tw-space-y-4">
            <h3 class="tw-font-semibold tw-text-lg">Major Class</h3>
            <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">
              Select lines of business for this query (same as under General).
            </p>
            @if (contractLinesOfBusiness.length === 0) {
              <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">No lines of business on this contract.</p>
            } @else {
              <div class="tw-flex tw-flex-col tw-gap-2">
                @for (lob of contractLinesOfBusiness; track lob) {
                  <app-checkbox
                    [control]="majorClassLobCheckboxControl(lob)"
                    [label]="lob"
                  />
                }
              </div>
            }
          </div>
        }
        @case ('WorkersComp') {
          <div class="tw-max-w-[960px] tw-space-y-4">
            <h3 class="tw-font-semibold tw-text-lg">Workers' Comp</h3>
            <div
              class="tw-flex tw-flex-wrap tw-gap-1 tw-border-b tw-border-[var(--sed-black-60-color)]"
              role="tablist"
              aria-label="Workers' Comp query options"
            >
              @for (t of wcLobTabs; track t.id) {
                <button
                  type="button"
                  role="tab"
                  [attr.aria-selected]="wcLobSubTab === t.id"
                  class="tw-px-3 tw-py-2 tw-text-sm tw-rounded-t tw-border tw-mb-[-1px] tw-transition-colors"
                  [class.tw-border-[var(--sed-black-60-color)]]="wcLobSubTab === t.id"
                  [class.tw-border-b-white]="wcLobSubTab === t.id"
                  [class.tw-bg-white]="wcLobSubTab === t.id"
                  [class.tw-font-semibold]="wcLobSubTab === t.id"
                  [class.tw-border-transparent]="wcLobSubTab !== t.id"
                  [class.tw-text-[var(--sed-black-60-color)]]="wcLobSubTab !== t.id"
                  (click)="onWcLobSubTabSelect(t.id)"
                >
                  {{ t.label }}
                </button>
              }
            </div>
            @if (wcLobSubTab === 'claimType') {
              <div class="tw-pt-2">
                @if (wcClaimTypeSectionVisible) {
                  <ng-container *ngTemplateOutlet="wcClaimTypeTpl" />
                } @else {
                  <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">
                    Select Workers' Comp in Lines of Business under General to set WC claim types.
                  </p>
                }
              </div>
            }
            @if (wcLobSubTab === 'injuryCodes') {
              <div class="tw-pt-2 tw-space-y-8">
                <h3 class="tw-font-semibold tw-text-lg">Injury Codes</h3>
                <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">
                  Search by code or description, then move codes into the restricted lists (cause, nature, and body
                  part / target).
                </p>
                @for (inj of wcInjuryUiBlocks; track inj.kind) {
                  <div class="tw-space-y-3 tw-border-b tw-border-[var(--sed-black-60-color)] tw-pb-6 tw-last:tw-border-0">
                    <h4 class="tw-font-semibold tw-text-base">{{ inj.title }}</h4>
                    <div class="tw-flex tw-flex-wrap tw-gap-2 tw-items-end">
                      <app-input [control]="inj.codeCtrl" label="Code" />
                      <app-input [control]="inj.descCtrl" label="Description contains" classes="tw-min-w-[200px]" />
                      <app-button
                        label="Search"
                        emphasis="Low"
                        [loading]="wcInjurySearchLoading[inj.kind] === true"
                        (click)="runWcInjurySearch(inj.kind)"
                      />
                    </div>
                    <div class="tw-overflow-auto">
                      <table class="tw-w-full tw-text-sm tw-border-collapse">
                        <thead>
                          <tr>
                            <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Search Results</th>
                            <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                            <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Restrict to Following Codes:</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td class="tw-align-top tw-pr-2">
                              <label class="tw-sr-only" [attr.for]="'qp-wc-injury-' + inj.kind + '-search'"
                                >{{ inj.title }} search results</label
                              >
                              <select
                                [attr.id]="'qp-wc-injury-' + inj.kind + '-search'"
                                #availInj
                                multiple
                                class="tw-w-full tw-min-w-[200px] tw-h-[160px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                              >
                                @for (o of availableWcInjurySearchOptions(inj.kind); track o.value) {
                                  <option [value]="o.value">{{ o.label }}</option>
                                }
                              </select>
                            </td>
                            <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                              <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                                <app-button
                                  label="→"
                                  ariaLabel="Add selected"
                                  (click)="addSelectedWcInjury(inj.kind, availInj)"
                                />
                                <app-button label="⇒" ariaLabel="Add all" (click)="addAllWcInjurySearch(inj.kind)" />
                                <app-button
                                  label="←"
                                  ariaLabel="Remove selected"
                                  (click)="removeSelectedWcInjury(inj.kind, restInj)"
                                />
                                <app-button label="⇐" ariaLabel="Remove all" (click)="removeAllWcInjury(inj.kind)" />
                              </div>
                            </td>
                            <td class="tw-align-top tw-pl-2">
                              <label class="tw-sr-only" [attr.for]="'qp-wc-injury-' + inj.kind + '-restrict'"
                                >{{ inj.title }} restricted codes</label
                              >
                              <select
                                [attr.id]="'qp-wc-injury-' + inj.kind + '-restrict'"
                                #restInj
                                multiple
                                class="tw-w-full tw-min-w-[200px] tw-h-[160px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                              >
                                @for (o of restrictedWcInjuryOptions(inj.kind); track o.value) {
                                  <option [value]="o.value">{{ o.label }}</option>
                                }
                              </select>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                }
              </div>
            }
            @if (wcLobSubTab === 'icdCodes') {
              <div class="tw-pt-2">
                <ng-container *ngTemplateOutlet="icdCodesTpl" />
              </div>
            }
            @if (wcLobSubTab === 'workStatus') {
              <div class="tw-pt-2">
                <ng-container *ngTemplateOutlet="queryWorkStatusTpl" />
              </div>
            }
          </div>
        }
        @case ('GeneralLiability') {
          <app-query-major-liability-lob
            [lobApi]="'GL'"
            [formKey]="'majorGl'"
            [extended]="extended"
            [contractNumber]="contractNumber"
            [queryUniqueKey]="queryUniqueKey"
          />
        }
        @case ('AutoLiability') {
          <app-query-major-liability-lob
            [lobApi]="'AU'"
            [formKey]="'majorAl'"
            [extended]="extended"
            [contractNumber]="contractNumber"
            [queryUniqueKey]="queryUniqueKey"
          />
        }
        @case ('PropertyLiability') {
          <app-query-major-liability-lob
            [lobApi]="'PR'"
            [formKey]="'majorPl'"
            [extended]="extended"
            [contractNumber]="contractNumber"
            [queryUniqueKey]="queryUniqueKey"
          />
        }
        @case ('Disability') {
          <div class="tw-space-y-4 tw-max-w-[960px]">
            <h3 class="tw-font-semibold tw-text-lg">Disability</h3>
            <div
              class="tw-flex tw-flex-wrap tw-gap-1 tw-border-b tw-border-[var(--sed-black-60-color)]"
              role="tablist"
              aria-label="Disability query options"
            >
              @for (t of dsLobTabs; track t.id) {
                <button
                  type="button"
                  role="tab"
                  [attr.aria-selected]="dsLobSubTab === t.id"
                  class="tw-px-3 tw-py-2 tw-text-sm tw-rounded-t tw-border tw-mb-[-1px] tw-transition-colors"
                  [class.tw-border-[var(--sed-black-60-color)]]="dsLobSubTab === t.id"
                  [class.tw-border-b-white]="dsLobSubTab === t.id"
                  [class.tw-bg-white]="dsLobSubTab === t.id"
                  [class.tw-font-semibold]="dsLobSubTab === t.id"
                  [class.tw-border-transparent]="dsLobSubTab !== t.id"
                  [class.tw-text-[var(--sed-black-60-color)]]="dsLobSubTab !== t.id"
                  (click)="onDsLobSubTabSelect(t.id)"
                >
                  {{ t.label }}
                </button>
              }
            </div>
            @if (dsLobSubTab === 'misc') {
              <app-query-disability-lob
                [extended]="extended"
                [queryForm]="queryForm"
                [queryUniqueKey]="queryUniqueKey"
                section="misc"
              />
            }
            @if (dsLobSubTab === 'claim') {
              <app-query-disability-lob
                [extended]="extended"
                [queryForm]="queryForm"
                [queryUniqueKey]="queryUniqueKey"
                section="claim"
              />
            }
            @if (dsLobSubTab === 'icdCodes') {
              <div class="tw-pt-2">
                <ng-container *ngTemplateOutlet="icdCodesTpl" />
              </div>
            }
            @if (dsLobSubTab === 'workStatus') {
              <div class="tw-pt-2">
                <ng-container *ngTemplateOutlet="queryWorkStatusTpl" />
              </div>
            }
          </div>
        }
        @default {
          <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">
            Select a section from the menu.
          </p>
        }
      }
    </div>

    <ng-template #wcClaimTypeTpl>
      <div [formGroup]="wcFg" class="tw-space-y-3">
        <h3 class="tw-font-semibold tw-text-lg">WC Claim Type:</h3>
        @if (wcClaimTypesLoading) {
          <p class="tw-text-sm tw-text-[var(--sed-black-60-color)]">Loading claim types…</p>
        } @else {
          <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4">
            <div class="tw-flex tw-flex-col tw-gap-2 tw-min-w-0">
              <app-checkbox [control]="$any(wcFg.controls['indemnity'])" label="Indemnity" />
              <div>
                <label class="tw-sr-only" for="qp-wc-claim-indemnity-subtypes"
                  >Indemnity claim subtypes</label
                >
                <select
                  id="qp-wc-claim-indemnity-subtypes"
                  multiple
                  class="tw-min-h-[120px] tw-w-full tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                  [formControl]="$any(wcFg.controls['indemnityIds'])"
                >
                  @for (o of indemnityOptions; track wcClaimTypeOptionTrack($index, o)) {
                    <option [ngValue]="o.value">{{ wcSubtypeOptionLabel(o) }}</option>
                  }
                </select>
              </div>
            </div>
            <div class="tw-flex tw-flex-col tw-gap-2 tw-min-w-0">
              <app-checkbox [control]="$any(wcFg.controls['medical'])" label="Medical" />
              <div>
                <label class="tw-sr-only" for="qp-wc-claim-medical-subtypes"
                  >Medical claim subtypes</label
                >
                <select
                  id="qp-wc-claim-medical-subtypes"
                  multiple
                  class="tw-min-h-[120px] tw-w-full tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                  [formControl]="$any(wcFg.controls['medicalIds'])"
                >
                  @for (o of medicalOptions; track wcClaimTypeOptionTrack($index, o)) {
                    <option [ngValue]="o.value">{{ wcSubtypeOptionLabel(o) }}</option>
                  }
                </select>
              </div>
            </div>
            <div class="tw-flex tw-flex-col tw-gap-2 tw-min-w-0">
              <app-checkbox [control]="$any(wcFg.controls['incident'])" label="Incident" />
              <div>
                <label class="tw-sr-only" for="qp-wc-claim-incident-subtypes"
                  >Incident claim subtypes</label
                >
                <select
                  id="qp-wc-claim-incident-subtypes"
                  multiple
                  class="tw-min-h-[120px] tw-w-full tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                  [formControl]="$any(wcFg.controls['incidentIds'])"
                >
                  @for (o of incidentOptions; track wcClaimTypeOptionTrack($index, o)) {
                    <option [ngValue]="o.value">{{ wcSubtypeOptionLabel(o) }}</option>
                  }
                </select>
              </div>
            </div>
            <div class="tw-flex tw-flex-col tw-gap-2 tw-min-w-0">
              <app-checkbox [control]="$any(wcFg.controls['employersLiability'])" label="Employer's Liability" />
              <div>
                <label class="tw-sr-only" for="qp-wc-claim-liability-subtypes"
                  >Employer's liability claim subtypes</label
                >
                <select
                  id="qp-wc-claim-liability-subtypes"
                  multiple
                  class="tw-min-h-[120px] tw-w-full tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                  [formControl]="$any(wcFg.controls['liabilityIds'])"
                >
                  @for (o of liabilityOptions; track wcClaimTypeOptionTrack($index, o)) {
                    <option [ngValue]="o.value">{{ wcSubtypeOptionLabel(o) }}</option>
                  }
                </select>
              </div>
            </div>
          </div>
        }
      </div>
    </ng-template>

    <ng-template #queryWorkStatusTpl>
      <div class="tw-space-y-5 tw-max-w-[800px]" [formGroup]="queryWorkGroup">
        <h3 class="tw-font-semibold tw-text-lg">Work Status</h3>
        <div
          class="tw-w-full tw-italic tw-pb-2 tw-mb-1 tw-text-sm tw-text-[var(--sed-black-60-color)]"
        >
          Select a work status to return claims where the selected status is effective on the As of date in the query.
          Adding an effective date range will expand the results to include claims where the selected status is effective
          at any point within that timeframe. If multiple work statuses are selected, results will include any of them
          (e.g., Off work <strong class="tw-not-italic">or</strong> Restricted).
        </div>
        <div class="tw-flex tw-items-center tw-pb-2 tw-border-b tw-border-[var(--sed-black-60-color)]/70">
          <div class="tw-basis-[200px] tw-font-bold tw-text-sm">Work status</div>
          <div class="tw-font-bold tw-text-sm">Effective date</div>
        </div>
        <div class="tw-flex tw-items-start tw-pb-2">
          <div class="tw-basis-[200px] tw-pt-1">
            <app-checkbox [control]="$any(queryWorkGroup.controls['useNormal'])" label="Normal" />
          </div>
          <div class="tw-min-w-0 tw-flex-1">
            <app-range
              type="Date"
              [control]="$any(queryWorkGroup.controls['normalFrom'])"
              [toControl]="$any(queryWorkGroup.controls['normalTo'])"
              [showHelpText]="true"
              screenReaderLabel="Normal effective date"
            />
          </div>
        </div>
        <div class="tw-flex tw-items-start tw-pb-2">
          <div class="tw-basis-[200px] tw-pt-1">
            <app-checkbox [control]="$any(queryWorkGroup.controls['useOffWork'])" label="Off work" />
          </div>
          <div class="tw-min-w-0 tw-flex-1">
            <app-range
              type="Date"
              [control]="$any(queryWorkGroup.controls['offFrom'])"
              [toControl]="$any(queryWorkGroup.controls['offTo'])"
              [showHelpText]="true"
              screenReaderLabel="Off work effective date"
            />
          </div>
        </div>
        <div class="tw-flex tw-items-start tw-pb-2">
          <div class="tw-basis-[200px] tw-pt-1">
            <app-checkbox [control]="$any(queryWorkGroup.controls['useRestricted'])" label="Restricted" />
          </div>
          <div class="tw-min-w-0 tw-flex-1">
            <app-range
              type="Date"
              [control]="$any(queryWorkGroup.controls['restrictFrom'])"
              [toControl]="$any(queryWorkGroup.controls['restrictTo'])"
              [showHelpText]="true"
              screenReaderLabel="Restricted effective date"
            />
          </div>
        </div>
        <div class="tw-flex tw-items-start tw-pb-2">
          <div class="tw-basis-[200px] tw-pt-1">
            <app-checkbox [control]="$any(queryWorkGroup.controls['useNtl'])" label="Restricted-NTL" />
          </div>
          <div class="tw-min-w-0 tw-flex-1">
            <app-range
              type="Date"
              [control]="$any(queryWorkGroup.controls['ntlFrom'])"
              [toControl]="$any(queryWorkGroup.controls['ntlTo'])"
              [showHelpText]="true"
              screenReaderLabel="Restricted-NTL effective date"
            />
          </div>
        </div>
        <div class="tw-flex tw-items-start tw-pb-2 tw-border-b tw-border-[var(--sed-black-60-color)]/70">
          <div class="tw-basis-[200px] tw-pt-1">
            <app-checkbox [control]="$any(queryWorkGroup.controls['useTerminated'])" label="Terminated" />
          </div>
          <div class="tw-min-w-0 tw-flex-1">
            <app-range
              type="Date"
              [control]="$any(queryWorkGroup.controls['termFrom'])"
              [toControl]="$any(queryWorkGroup.controls['termTo'])"
              [showHelpText]="true"
              screenReaderLabel="Terminated effective date"
            />
          </div>
        </div>
        <div class="tw-space-y-2">
          <label class="tw-font-bold tw-text-sm tw-block" for="queryWorkTermReasonSelect">Terminated Reasons:</label>
          <select
            id="queryWorkTermReasonSelect"
            class="tw-w-full tw-max-w-md tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm tw-bg-[var(--sed-white-color)]"
            [formControl]="$any(queryWorkGroup.controls['termReason'])"
          >
            <option value="">(none)</option>
            @for (o of queryWorkTerminatedReasonOptions; track o.value) {
              <option [value]="o.value">{{ o.label }}</option>
            }
          </select>
        </div>
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 tw-pt-2">
          <app-input [control]="$any(queryWorkGroup.controls['approveMin'])" label="Approve Min" />
          <app-input [control]="$any(queryWorkGroup.controls['approveMax'])" label="Approve Max" />
          <app-input [control]="$any(queryWorkGroup.controls['denyMin'])" label="Deny Min" />
          <app-input [control]="$any(queryWorkGroup.controls['denyMax'])" label="Deny Max" />
          <app-input [control]="$any(queryWorkGroup.controls['restrictMin'])" label="Restrict Min" />
          <app-input [control]="$any(queryWorkGroup.controls['restrictMax'])" label="Restrict Max" />
          <app-input [control]="$any(queryWorkGroup.controls['ntlMin'])" label="NTL Min" />
          <app-input [control]="$any(queryWorkGroup.controls['ntlMax'])" label="NTL Max" />
          <app-input [control]="$any(queryWorkGroup.controls['pendMin'])" label="Pend Min" />
          <app-input [control]="$any(queryWorkGroup.controls['pendMax'])" label="Pend Max" />
        </div>
      </div>
    </ng-template>

    <ng-template #icdCodesTpl>
      <div class="tw-max-w-[900px] tw-space-y-4">
        <h3 class="tw-font-semibold tw-text-lg">ICD Codes</h3>
        <div class="tw-overflow-auto">
          <table class="tw-w-full tw-text-sm tw-border-collapse">
            <thead>
              <tr>
                <th scope="col" class="tw-sr-only">Search criterion</th>
                <th scope="col" class="tw-sr-only">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th
                  scope="row"
                  id="qp-icd9-tpl-version-label"
                  class="tw-py-1 tw-pr-4 tw-align-top tw-font-medium tw-whitespace-nowrap tw-text-left tw-font-normal"
                >
                  ICD version
                </th>
                <td class="tw-py-1">
                  <select
                    aria-labelledby="qp-icd9-tpl-version-label"
                    [formControl]="icdVersionSelect"
                    class="tw-min-w-[140px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                  >
                    <option value="">All ICDs</option>
                    <option value="10">ICD10</option>
                    <option value="9">ICD9</option>
                  </select>
                </td>
              </tr>
              <tr>
                <th scope="row" class="tw-py-1 tw-pr-4 tw-align-top tw-font-medium tw-whitespace-nowrap tw-text-left tw-font-normal">Code begins</th>
                <td class="tw-py-1">
                  <div class="tw-flex tw-flex-wrap tw-gap-2 tw-items-center">
                    <select
                      [formControl]="icdCodePrefixSelect"
                      class="tw-min-w-[88px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                      aria-label="First character of code"
                    >
                      <option value="">Any</option>
                      @for (c of icdCodePrefixChars; track c) {
                        <option [value]="c">{{ c }}</option>
                      }
                    </select>
                    <input
                      type="text"
                      [formControl]="icdCodeBeginsExtra"
                      class="tw-min-w-[160px] tw-flex-1 tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                      placeholder="Additional prefix"
                      aria-label="Additional code prefix"
                    />
                    <app-button
                      label="Search"
                      ariaLabel="Search ICD codes"
                      [loading]="icdSearchLoading"
                      (click)="runIcdSearch()"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <th scope="row" class="tw-py-1 tw-pr-4 tw-align-top tw-font-medium tw-whitespace-nowrap tw-text-left tw-font-normal">
                  Description contains
                </th>
                <td class="tw-py-1">
                  <input
                    type="text"
                    [formControl]="icdDescriptionContains"
                    class="tw-w-full tw-max-w-md tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-text-sm"
                    aria-label="Description contains"
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <table class="tw-w-full tw-text-sm tw-border-collapse tw-mt-4">
            <thead>
              <tr>
                <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pr-2">Search Results</th>
                <th scope="col" class="tw-w-14 tw-p-2">
                        <span class="tw-sr-only">Add or remove between lists</span>
                      </th>
                <th scope="col" class="tw-text-left tw-font-semibold tw-pb-2 tw-pl-2">Restrict to Following Codes:</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="tw-align-top tw-pr-2">
                  <app-query-virtual-dual-list
                    listId="qp-icd-tpl-search-results"
                    ariaLabel="ICD code search results"
                    [options]="icdDualLeftOptions"
                    [selection]="icdLeftSelection"
                  />
                </td>
                <td class="tw-align-middle tw-px-1 tw-align-top tw-pt-2">
                  <div class="tw-flex tw-flex-col tw-gap-2 tw-items-center">
                    <app-button
                      label="→"
                      ariaLabel="Add selected ICD codes"
                      (click)="addSelectedIcdFromSearch()"
                    />
                    <app-button
                      label="⇒"
                      ariaLabel="Add all search results"
                      (click)="addAllIcdFromSearch()"
                    />
                    <app-button
                      label="←"
                      ariaLabel="Remove selected ICD codes"
                      (click)="removeSelectedIcd()"
                    />
                    <app-button
                      label="⇐"
                      ariaLabel="Remove all ICD codes"
                      (click)="removeAllIcd()"
                    />
                  </div>
                </td>
                <td class="tw-align-top tw-pl-2">
                  <app-query-virtual-dual-list
                    listId="qp-icd-tpl-restricted"
                    ariaLabel="Restricted ICD codes"
                    [options]="icdDualRightOptions"
                    [selection]="icdRightSelection"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </ng-template>

  `,
})
export class QueryParameterPanelHostComponent extends BaseComponent implements OnChanges, OnInit {
  @Input({ required: true }) panelKey!: ParameterSideMenuKey;
  @Input({ required: true }) queryForm!: FormGroup;
  @Input({ required: true }) extended!: FormGroup;
  @Input({ required: true }) contractNumber!: number | null;
  @Input() queryUniqueKey: number | null = null;
  /**
   * Bumped when the parent reloads query detail for edit so sidebar parameter GETs run again
   * (otherwise `loadedParameterResources` skips refetch for the same `queryUniqueKey`).
   */
  @Input() parameterPanelRefreshToken = 0;
  /** PKG_QUERY.GetLocationSelectionType_p when loading query detail for edit (A | U | S). */
  @Input() locationSelectionMethodCode: string | null = null;
  @Input() queryType = 'C';
  /** Legacy QueryWizardSimpleType: visibility matches contract LOBs + selected LOBs */
  @Input() contractLinesOfBusiness: string[] = [];

  get wcClaimTypeSectionVisible(): boolean {
    const selected = (this.queryForm.controls['linesOfBusiness']?.value ?? []) as string[];
    return isClaimTypeLobSectionVisible('WorkersComp', selected, this.contractLinesOfBusiness, this.queryType);
  }

  get dsClaimTypeSectionVisible(): boolean {
    const selected = (this.queryForm.controls['linesOfBusiness']?.value ?? []) as string[];
    return isClaimTypeLobSectionVisible('Disability', selected, this.contractLinesOfBusiness, this.queryType);
  }

  /** Legacy QueryWizardSimpleStates: jurisdiction + loss only; payroll is QueryWizardAdvancedClaimStates / Claims → States. */
  get showPayrollStatesInClaimStates(): boolean {
    return this.panelKey !== 'States';
  }

  readonly wcSubtypeOptionLabel = formatWcSubtypeOptionLabel;

  readonly wcClaimTypeOptionTrack = wcClaimTypeOptionTrack;

  get wcFg(): FormGroup {
    return this.extended.get('wcClaimTypes') as FormGroup;
  }

  wcClaimTypesLoading = false;
  indemnityOptions: LobItem[] = [];
  medicalOptions: LobItem[] = [];
  incidentOptions: LobItem[] = [];
  liabilityOptions: LobItem[] = [];
  /** Avoid refetch when switching sidebar items away and back (same contract/query). */
  private wcClaimTypesLoadKey: string | null = null;

  /**
   * Shared GET endpoints keyed per contract/query session so revisiting a sidebar section does not
   * refetch and overwrite unsaved extended-form values (states used by both States and Claims, etc.).
   */
  private readonly loadedParameterResources = new Set<string>();

  private parameterResourcesSessionPrefix(): string {
    return `${this.contractNumber ?? 0}|${this.queryUniqueKey ?? 0}`;
  }

  private hasLoadedParameterResource(resourceKey: string): boolean {
    return this.loadedParameterResources.has(`${this.parameterResourcesSessionPrefix()}|${resourceKey}`);
  }

  private markParameterResourceLoaded(resourceKey: string): void {
    this.loadedParameterResources.add(`${this.parameterResourcesSessionPrefix()}|${resourceKey}`);
  }

  private resetLoadedParameterResources(): void {
    this.loadedParameterResources.clear();
  }

  statesLoading = false;
  flagsLoading = false;
  examinersLoading = false;
  bankingLoading = false;
  paymentsLoading = false;
  timeLoading = false;
  financialLoading = false;
  locationsLoading = false;
  vendorsLoading = false;
  paycodesLoading = false;

  /** Legacy simple payment: pay code search (SEARCHQUERYPAYCODES_P). */
  paycodeSearchPrefix = new FormControl<string>('', { nonNullable: true });
  paycodeSearchDescription = new FormControl<string>('', { nonNullable: true });
  paycodeSearchResultItems: PaycodeItem[] = [];

  readonly payCodeSeriesDefs: { digit: string; label: string }[] = [
    { digit: '1', label: '100 Series (Indemnity/Loss)' },
    { digit: '2', label: '200 Series (Loss)' },
    { digit: '3', label: '300 Series (Medical)' },
    { digit: '4', label: '400 Series (Loss)' },
    { digit: '5', label: '500 Series (Loss)' },
    { digit: '6', label: '600 Series (Expense)' },
    { digit: '7', label: '700 Series (Recovery)' },
  ];

  stateOptions: { label: string; value: string }[] = [];
  readonly claimFlagFieldDefs = QUERY_CLAIM_FLAG_FIELD_DEFS;
  examinerOfficeOptions: { label: string; value: string }[] = [];
  processorOfficeOptions: { label: string; value: string }[] = [];
  bankingOptions: { label: string; value: string }[] = [];
  paymentRows: { code: string; name: string; selected: boolean }[] = [];
  private paymentCheckboxControls = new Map<string, FormControl<boolean>>();
  private paySeriesCheckboxControls = new Map<string, FormControl<boolean>>();
  private majorClassLobCheckboxControls = new Map<string, FormControl<boolean>>();
  accountOptions: { label: string; value: string }[] = [];
  vendorOptions: { label: string; value: string }[] = [];
  paycodeOptions: { label: string; value: string }[] = [];

  get paymentRowsPaymentTypesOnly(): { code: string; name: string; selected: boolean }[] {
    return this.paymentRows.filter((r) => !this.isDocumentPaymentCode(r.code));
  }

  get paymentRowsDocumentTypesOnly(): { code: string; name: string; selected: boolean }[] {
    return this.paymentRows.filter((r) => this.isDocumentPaymentCode(r.code));
  }

  get availablePaycodeSearchDualList(): { label: string; value: string }[] {
    const sel = new Set((this.extended.controls['paycodeExpressions'].value ?? []) as string[]);
    const seen = new Set<string>();
    const out: { label: string; value: string }[] = [];
    for (const p of this.paycodeSearchResultItems) {
      const id = p.paycodeId ? String(p.paycodeId) : '';
      if (!id || sel.has(id) || seen.has(id)) {
        continue;
      }
      seen.add(id);
      out.push({ label: this.paycodeItemLabel(p), value: id });
    }
    return out.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }));
  }

  get restrictedPaycodeDualList(): { label: string; value: string }[] {
    const seriesDigits = [...new Set((this.extended.get('paycodeSeries')?.value ?? []) as string[])]
      .map((d) => d.trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    const seriesRows = seriesDigits.map((digit) => ({
      label: this.paySeriesRestrictLabel(digit),
      value: digit,
    }));

    const raw = (this.extended.controls['paycodeExpressions'].value ?? []) as string[];
    const ids = [...new Set(raw.map((x) => String(x).trim()).filter(Boolean))].filter(
      (id) => !seriesDigits.some((d) => this.paycodeIdCoveredBySeriesDigit(id, d)),
    );
    const individualRows = ids.map((id) => {
      const o = this.paycodeOptions.find((x) => x.value === id);
      if (o) {
        return o;
      }
      const s = this.paycodeSearchResultItems.find((x) => String(x.paycodeId) === id);
      return s ? { label: this.paycodeItemLabel(s), value: id } : { label: id, value: id };
    });

    return [...seriesRows, ...individualRows];
  }

  /**
   * When "All 100 Series" (digit `1`) is selected, individual pay codes `101`, `102`, … are implied;
   * they must not appear as separate restricted rows (legacy stores both series + detail rows).
   */
  private paycodeIdCoveredBySeriesDigit(paycodeId: string, seriesDigit: string): boolean {
    const id = paycodeId.trim();
    const d = seriesDigit.trim();
    if (!id || !d) {
      return false;
    }
    if (id === d) {
      return true;
    }
    return id.startsWith(d);
  }

  /** Legacy restricted row: e.g. `2 - All 200 Series` (not one option per pay code). */
  private paySeriesRestrictLabel(digit: string): string {
    const def = this.payCodeSeriesDefs.find((d) => d.digit === digit);
    const base = def
      ? def.label.replace(/\s*\([^)]*\)\s*$/, '').trim()
      : `${digit}00 Series`;
    return `${digit} - All ${base}`;
  }

  /** Matches server `GetPaymentsHandler.IsDocumentTypeOption` (Checks / Vouchers only). */
  isDocumentPaymentCode(code: string): boolean {
    const c = code.trim().toLowerCase();
    return c === 'checks' || c === 'vouchers';
  }

  claimFlagControl(formKey: QueryClaimFlagFormKey): FormControl<string> {
    return (this.extended.get('flags') as FormGroup).controls[
      formKey
    ] as FormControl<string>;
  }

  claimFlagOptions(flagLabel: string): { label: string; value: string }[] {
    return queryClaimFlagSelectOptions(flagLabel);
  }

  /**
   * Payment type/document checkboxes: `paymentSelections` is one array control; use per-code
   * FormControls that sync with `paymentRows` and `setPaymentSelected`.
   */
  paymentCheckboxControlFor(code: string): FormControl<boolean> {
    let c = this.paymentCheckboxControls.get(code);
    if (!c) {
      const row = this.paymentRows.find((r) => r.code === code);
      c = new FormControl(row?.selected ?? false, { nonNullable: true });
      c.valueChanges.subscribe((v) => this.setPaymentSelected(code, v));
      this.paymentCheckboxControls.set(code, c);
    } else {
      const row = this.paymentRows.find((r) => r.code === code);
      if (row && c.value !== row.selected) {
        queueMicrotask(() => {
          const r2 = this.paymentRows.find((r) => r.code === code);
          if (r2 && c.value !== r2.selected) {
            c.setValue(r2.selected, { emitEvent: false });
          }
        });
      }
    }
    return c;
  }

  /** Pay code series checkboxes (extended `paycodeSeries` string[]). */
  paySeriesCheckboxControl(digit: string): FormControl<boolean> {
    let c = this.paySeriesCheckboxControls.get(digit);
    if (!c) {
      c = new FormControl(this.isPaySeriesSelected(digit), { nonNullable: true });
      c.valueChanges.subscribe((v) => {
        if (this.isPaySeriesSelected(digit) !== v) {
          this.setPaySeriesSelected(digit, v);
        }
      });
      this.paySeriesCheckboxControls.set(digit, c);
    } else {
      const sel = this.isPaySeriesSelected(digit);
      if (c.value !== sel) {
        queueMicrotask(() => {
          if (c.value !== this.isPaySeriesSelected(digit)) {
            c.setValue(this.isPaySeriesSelected(digit), { emitEvent: false });
          }
        });
      }
    }
    return c;
  }

  /** Major Class LOB toggles mirror `queryForm.linesOfBusiness`. */
  majorClassLobCheckboxControl(lob: string): FormControl<boolean> {
    let c = this.majorClassLobCheckboxControls.get(lob);
    if (!c) {
      c = new FormControl(this.isLobSelectedForMajorClass(lob), { nonNullable: true });
      c.valueChanges.subscribe((v) => this.toggleLobOnMajorClass(lob, v));
      this.majorClassLobCheckboxControls.set(lob, c);
    } else {
      const sel = this.isLobSelectedForMajorClass(lob);
      if (c.value !== sel) {
        queueMicrotask(() => {
          const next = this.isLobSelectedForMajorClass(lob);
          if (c.value !== next) {
            c.setValue(next, { emitEvent: false });
          }
        });
      }
    }
    return c;
  }

  timeTypeSelOpts: { label: string; value: string }[] = [];
  timeStatusSelOpts: { label: string; value: string }[] = [];
  timeReasonSelOpts: { label: string; value: string }[] = [];

  /** Legacy WC query wizard: Claim Type / Injury Codes / ICD Codes / Work Status */
  wcLobSubTab: WcLobSubTab = 'claimType';
  readonly wcLobTabs: { id: WcLobSubTab; label: string }[] = [
    { id: 'claimType', label: 'Claim Type' },
    { id: 'injuryCodes', label: 'Injury Codes' },
    { id: 'icdCodes', label: 'ICD Codes' },
    { id: 'workStatus', label: 'Work Status' },
  ];

  /** Legacy AdvancedMajorDisabilityTab: Miscellaneous first (default). */
  dsLobSubTab: DsLobSubTab = 'misc';
  readonly dsLobTabs: { id: DsLobSubTab; label: string }[] = [
    { id: 'misc', label: 'Miscellaneous' },
    { id: 'claim', label: 'Claim Type/Status' },
    { id: 'icdCodes', label: 'ICD Codes' },
    { id: 'workStatus', label: 'Work Status' },
  ];

  /** Legacy Advanced → Claims notebook tabs */
  claimsSubTab: ClaimsSubTab = 'general';
  readonly claimsTabs: { id: ClaimsSubTab; label: string }[] = [
    { id: 'general', label: 'General' },
    { id: 'states', label: 'States' },
    { id: 'vendors', label: 'Vendors' },
  ];

  /** Legacy Advanced → Payments: `AdvancedPaymentsTab.ascx` */
  advPaymentsSubTab: AdvPaymentsSubTab = 'general';
  readonly advPaymentsTabs: { id: AdvPaymentsSubTab; label: string }[] = [
    { id: 'general', label: 'General' },
    { id: 'payCodes', label: 'Pay Codes' },
  ];

  readonly advPaymentsClearedStatusOptions: { label: string; value: string }[] = [
    { label: 'Include All Payments', value: 'All' },
    { label: 'Include Only Outstanding Payments', value: 'Outstanding' },
    { label: 'Include Only Payments Cleared Between', value: 'ClearedInDateRange' },
  ];

  readonly advPaymentsEscheatedStatusOptions: { label: string; value: string }[] = [
    { label: 'Include All Payments', value: 'All' },
    { label: 'Include Only Non-Escheated Payments', value: 'NonEscheated' },
    { label: 'Include Only Payments Escheated Between', value: 'EscheatedInDateRange' },
  ];

  readonly reportIncludeTypeOptions: { label: string; value: string }[] = [
    { label: 'Claims only', value: 'ClaimOnly' },
    { label: 'Incidents only', value: 'IncidentOnly' },
    { label: 'Incidents and Claims', value: 'IncidentAndClaim' },
  ];

  injuryCauseCode = new FormControl<string>('', { nonNullable: true });
  injuryCauseDesc = new FormControl<string>('', { nonNullable: true });
  injuryNatureCode = new FormControl<string>('', { nonNullable: true });
  injuryNatureDesc = new FormControl<string>('', { nonNullable: true });
  injuryPartCode = new FormControl<string>('', { nonNullable: true });
  injuryPartDesc = new FormControl<string>('', { nonNullable: true });
  wcInjuryCauseSearchRows: QueryWcInjuryCodeItem[] = [];
  wcInjuryNatureSearchRows: QueryWcInjuryCodeItem[] = [];
  wcInjuryPartSearchRows: QueryWcInjuryCodeItem[] = [];
  wcInjurySearchLoading: Record<string, boolean> = {};
  private readonly wcInjuryLabelById = new Map<string, string>();
  /** Loaded injury selection rows from GETQUERYINJURY_P (avoid duplicate fetches). */
  private wcInjurySelectionLoadKey: string | null = null;
  queryWorkTerminatedReasonOptions: { label: string; value: string }[] = [];

  get wcInjuryUiBlocks(): {
    kind: 'cause' | 'nature' | 'part';
    title: string;
    codeCtrl: FormControl<string>;
    descCtrl: FormControl<string>;
  }[] {
    return [
      {
        kind: 'cause',
        title: 'Cause of injury',
        codeCtrl: this.injuryCauseCode,
        descCtrl: this.injuryCauseDesc,
      },
      {
        kind: 'nature',
        title: 'Nature of injury',
        codeCtrl: this.injuryNatureCode,
        descCtrl: this.injuryNatureDesc,
      },
      {
        kind: 'part',
        title: 'Body part / target',
        codeCtrl: this.injuryPartCode,
        descCtrl: this.injuryPartDesc,
      },
    ];
  }

  get queryWorkGroup(): FormGroup {
    return this.extended.get('queryWork') as FormGroup;
  }

  /** Advanced Payments (page 1) — amounts, cleared/escheated. */
  get advancedPaymentsGroup(): FormGroup {
    return this.extended.get('advancedPayments') as FormGroup;
  }

  /** Legacy QueryWizardAdvancedLocation: Method For Selecting Locations */
  locationSelectionMethod = new FormControl<'Accounts' | 'Units' | 'Structure'>('Accounts', { nonNullable: true });
  readonly locationMethodOptions: { label: string; value: 'Accounts' | 'Units' | 'Structure' }[] = [
    { label: 'Select Accounts and Units', value: 'Accounts' },
    { label: 'Select Specific Units', value: 'Units' },
    { label: 'Select Locations Using Structure', value: 'Structure' },
  ];
  locationSearchUnitNumber = new FormControl<string>('', { nonNullable: true });
  locationSearchStructCode = new FormControl<string>('', { nonNullable: true });
  locationSearchLocationName = new FormControl<string>('', { nonNullable: true });
  locationSearchAddress = new FormControl<string>('', { nonNullable: true });
  locationSearchCity = new FormControl<string>('', { nonNullable: true });
  locationSearchState = new FormControl<string>('', { nonNullable: true });
  locationSearchZip = new FormControl<string>('', { nonNullable: true });
  locationSearchLoading = false;
  unitsSearchResults: LocationUnitItem[] = [];
  /** Account IDs with expanded unit list */
  expandedAccounts: string[] = [];
  /** Units loaded per account (Accounts mode) */
  unitsByAccountCache = new Map<string, { label: string; value: string }[]>();
  accountUnitsLoading: Record<string, boolean> = {};
  private readonly unitLabelByCompositeKey = new Map<string, string>();
  /** Corporate structure tree (GETLOCATIONSTRUCTURE_P via filters criteria). */
  structureTreeNodes: TreeNode[] = [];
  structureTreeLoading = false;
  private structureRootLoadedContract: number | null = null;
  structureSearchName = new FormControl<string>('', { nonNullable: true });
  structureSearchIncludeIntermediate = new FormControl<boolean>(false, { nonNullable: true });
  structureSearchLoading = false;
  structureSearchRows: CorpStructNodeItem[] = [];
  /** Single ID field for legacy Examiner "Add" (→). */
  examinerIdDraft = new FormControl<string>('', { nonNullable: true });

  icdSearchLoading = false;
  icdSearchResultItems: {
    code: string;
    description: string;
    version: string;
    displayLabel: string;
  }[] = [];
  private icdDescriptionByCode: Record<string, string> = {};
  /** Full list line per selected code (matches API <c>displayLabel</c> when added from search). */
  private icdDisplayLabelByCode: Record<string, string> = {};
  /** Invalidate in-flight ICD label hydration when query/contract resets (see `hydrateIcdDisplayLabelsForSelectedCodes`). */
  private icdHydrateGen = 0;
  /** Search Results column (virtual list source). */
  icdDualLeftOptions: { label: string; value: string }[] = [];
  /** Restrict to Following Codes column (virtual list source). */
  icdDualRightOptions: { label: string; value: string }[] = [];
  /** Multi-select: Ctrl/Cmd+click toggles; plain click selects one row. */
  readonly icdLeftSelection = new Set<string>();
  readonly icdRightSelection = new Set<string>();
  readonly icdCodePrefixChars = [
    ...'0123456789'.split(''),
    ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
  ];
  icdVersionSelect = new FormControl<string>('', { nonNullable: true });
  icdCodePrefixSelect = new FormControl<string>('', { nonNullable: true });
  icdCodeBeginsExtra = new FormControl<string>('', { nonNullable: true });
  icdDescriptionContains = new FormControl<string>('', { nonNullable: true });

  get timeGroup(): FormGroup {
    return this.extended.get('timeTracking') as FormGroup;
  }

  /** Legacy dual-list: left column = options not yet restricted (QueryWizardSimpleTime / AdvancedTime). */
  get timeTypeAvailableDualList(): { label: string; value: string }[] {
    const sel = new Set((this.timeGroup.controls['benefitTypes'].value ?? []) as string[]);
    return (this.timeTypeSelOpts ?? []).filter((o) => !sel.has(String(o.value)));
  }

  get timeTypeRestrictedDualList(): { label: string; value: string }[] {
    const sel = new Set((this.timeGroup.controls['benefitTypes'].value ?? []) as string[]);
    return (this.timeTypeSelOpts ?? []).filter((o) => sel.has(String(o.value)));
  }

  get timeStatusAvailableDualList(): { label: string; value: string }[] {
    const sel = new Set((this.timeGroup.controls['benefitStatuses'].value ?? []) as string[]);
    return (this.timeStatusSelOpts ?? []).filter((o) => !sel.has(String(o.value)));
  }

  get timeStatusRestrictedDualList(): { label: string; value: string }[] {
    const sel = new Set((this.timeGroup.controls['benefitStatuses'].value ?? []) as string[]);
    return (this.timeStatusSelOpts ?? []).filter((o) => sel.has(String(o.value)));
  }

  get timeReasonAvailableDualList(): { label: string; value: string }[] {
    const sel = new Set((this.timeGroup.controls['benefitReasons'].value ?? []) as string[]);
    return (this.timeReasonSelOpts ?? []).filter((o) => !sel.has(String(o.value)));
  }

  get timeReasonRestrictedDualList(): { label: string; value: string }[] {
    const sel = new Set((this.timeGroup.controls['benefitReasons'].value ?? []) as string[]);
    return (this.timeReasonSelOpts ?? []).filter((o) => sel.has(String(o.value)));
  }

  get financialsFormGroup(): FormGroup {
    return this.extended.get('financials') as FormGroup;
  }

  /** Offices not yet in the restricted list (legacy dual-list left column). */
  get availableExaminerOfficeOptions(): { label: string; value: string }[] {
    const ids = new Set((this.extended.controls['examinerOfficeIds'].value ?? []) as string[]);
    return this.examinerOfficeOptions.filter((o) => !ids.has(String(o.value)));
  }

  /** Offices currently selected (right column). */
  get restrictedExaminerOfficeOptions(): { label: string; value: string }[] {
    const ids = new Set((this.extended.controls['examinerOfficeIds'].value ?? []) as string[]);
    return this.examinerOfficeOptions.filter((o) => ids.has(String(o.value)));
  }

  get availableProcessorOfficeOptions(): { label: string; value: string }[] {
    const ids = new Set((this.extended.controls['processorOfficeIds'].value ?? []) as string[]);
    return this.processorOfficeOptions.filter((o) => !ids.has(String(o.value)));
  }

  get restrictedProcessorOfficeOptions(): { label: string; value: string }[] {
    const ids = new Set((this.extended.controls['processorOfficeIds'].value ?? []) as string[]);
    return this.processorOfficeOptions.filter((o) => ids.has(String(o.value)));
  }

  get availableBankingOptionsForDualList(): { label: string; value: string }[] {
    const ids = new Set((this.extended.controls['bankingBankNumbers'].value ?? []) as string[]);
    return this.bankingOptions.filter((o) => !ids.has(String(o.value)));
  }

  get restrictedBankingOptionsForDualList(): { label: string; value: string }[] {
    const ids = new Set((this.extended.controls['bankingBankNumbers'].value ?? []) as string[]);
    return this.bankingOptions.filter((o) => ids.has(String(o.value)));
  }

  get examinerIdsArray(): string[] {
    return (this.extended.controls['examinerIds'].value ?? []) as string[];
  }

  ngOnInit(): void {
    const fin = this.extended.get('financials');
    if (fin) {
      this.dispose.subs = fin.valueChanges.subscribe(() => {
        this.extended.get('includeFinancials')?.setValue(true, { emitEvent: false });
      });
    }

    const wcg = this.extended.get('wcClaimTypes') as FormGroup;
    this.dispose.subs = wcg.controls['indemnity'].valueChanges.subscribe((on) => {
      const ids = wcg.controls['indemnityIds'];
      if (on) {
        ids.enable({ emitEvent: false });
        const cur = (ids.value ?? []) as (string | null)[];
        if (cur.length === 0) {
          ids.setValue([WC_LEGACY_ALL_SUBTYPES_VALUE], { emitEvent: false });
        }
      } else {
        ids.setValue([], { emitEvent: false });
        ids.disable({ emitEvent: false });
      }
    });
    this.dispose.subs = wcg.controls['medical'].valueChanges.subscribe((on) => {
      const ids = wcg.controls['medicalIds'];
      if (on) {
        ids.enable({ emitEvent: false });
        const cur = (ids.value ?? []) as (string | null)[];
        if (cur.length === 0) {
          ids.setValue([WC_LEGACY_ALL_SUBTYPES_VALUE], { emitEvent: false });
        }
      } else {
        ids.setValue([], { emitEvent: false });
        ids.disable({ emitEvent: false });
      }
    });
    this.dispose.subs = wcg.controls['incident'].valueChanges.subscribe((on) => {
      const ids = wcg.controls['incidentIds'];
      if (on) {
        ids.enable({ emitEvent: false });
        const cur = (ids.value ?? []) as (string | null)[];
        if (cur.length === 0) {
          ids.setValue([WC_LEGACY_ALL_SUBTYPES_VALUE], { emitEvent: false });
        }
      } else {
        ids.setValue([], { emitEvent: false });
        ids.disable({ emitEvent: false });
      }
    });
    this.dispose.subs = wcg.controls['employersLiability'].valueChanges.subscribe((on) => {
      const ids = wcg.controls['liabilityIds'];
      if (on) {
        ids.enable({ emitEvent: false });
        const cur = (ids.value ?? []) as (string | null)[];
        if (cur.length === 0) {
          ids.setValue([WC_LEGACY_ALL_SUBTYPES_VALUE], { emitEvent: false });
        }
      } else {
        ids.setValue([], { emitEvent: false });
        ids.disable({ emitEvent: false });
      }
    });
    this.syncWcClaimSubtypeControlsEnabled();

    this.dispose.subs = this.locationSelectionMethod.valueChanges.subscribe((m) => {
      if (m === 'Structure' && this.contractNumber) {
        void this.ensureStructureRootLoaded();
        this.extended.controls['accountUnitIds'].setValue([], { emitEvent: false });
      } else {
        this.extended.controls['structureNodeIds'].setValue([], { emitEvent: false });
      }
    });

    const qwg = this.extended.get('queryWork');
    if (qwg) {
      this.dispose.subs = qwg.valueChanges.subscribe(() => {
        this.extended.get('includeQueryWork')?.setValue(true, { emitEvent: false });
      });
    }
    const markInjury = (): void => {
      this.extended.get('includeWcInjury')?.setValue(true, { emitEvent: false });
    };
    const c1 = this.extended.get('wcInjuryCauseIds');
    if (c1) {
      this.dispose.subs = c1.valueChanges.subscribe(markInjury);
    }
    const c2 = this.extended.get('wcInjuryNatureIds');
    if (c2) {
      this.dispose.subs = c2.valueChanges.subscribe(markInjury);
    }
    const c3 = this.extended.get('wcInjuryPartTargetIds');
    if (c3) {
      this.dispose.subs = c3.valueChanges.subscribe(markInjury);
    }

    const icdCtl = this.extended.controls['icdCodes'];
    this.dispose.subs = icdCtl.valueChanges.subscribe(() => {
      this.refreshIcdDualListOptions();
      queueMicrotask(() => void this.hydrateIcdDisplayLabelsForSelectedCodes());
    });
    this.refreshIcdDualListOptions();
    queueMicrotask(() => void this.hydrateIcdDisplayLabelsForSelectedCodes());
  }

  ngOnChanges(changes: SimpleChanges): void {
    const parameterRefreshTokenChanged =
      !!changes['parameterPanelRefreshToken'] &&
      changes['parameterPanelRefreshToken'].previousValue !== changes['parameterPanelRefreshToken'].currentValue;

    if (changes['contractNumber']) {
      this.structureRootLoadedContract = null;
      this.structureTreeNodes = [];
      this.structureSearchRows = [];
    }
    if (changes['contractNumber'] || changes['queryUniqueKey'] || parameterRefreshTokenChanged) {
      this.wcInjurySelectionLoadKey = null;
      this.wcInjuryCauseSearchRows = [];
      this.wcInjuryNatureSearchRows = [];
      this.wcInjuryPartSearchRows = [];
      this.wcInjuryLabelById.clear();
      this.queryWorkTerminatedReasonOptions = [];
    }
    if (changes['contractNumber'] || changes['queryUniqueKey'] || parameterRefreshTokenChanged) {
      this.resetLoadedParameterResources();
      this.wcClaimTypesLoadKey = null;
      this.icdSearchResultItems = [];
      this.icdDescriptionByCode = {};
      this.icdDisplayLabelByCode = {};
      this.icdHydrateGen++;
      this.icdLeftSelection.clear();
      this.icdRightSelection.clear();
      this.refreshIcdDualListOptions();
      queueMicrotask(() => void this.hydrateIcdDisplayLabelsForSelectedCodes());
    }
    if (changes['locationSelectionMethodCode']) {
      this.applyLocationSelectionMethodFromQueryDetail();
    }
    if (
      changes['panelKey'] ||
      changes['contractNumber'] ||
      changes['queryUniqueKey'] ||
      parameterRefreshTokenChanged
    ) {
      void this.refreshPanelData();
    }
    if (changes['panelKey'] && (this.panelKey === 'Examiner' || this.panelKey === 'ExaminersOffices')) {
      this.examinerIdDraft.setValue('', { emitEvent: false });
    }
    if (changes['panelKey'] && this.panelKey === 'WorkersComp') {
      this.wcLobSubTab = 'claimType';
    }
    if (changes['panelKey'] && this.panelKey === 'Disability') {
      this.dsLobSubTab = 'misc';
    }
    if (changes['panelKey'] && this.panelKey === 'Claims') {
      this.claimsSubTab = 'general';
    }
    if (changes['panelKey'] && this.panelKey === 'Payments') {
      this.advPaymentsSubTab = 'general';
    }
  }

  onClaimsSubTabSelect(id: ClaimsSubTab): void {
    this.claimsSubTab = id;
  }

  onAdvPaymentsSubTabSelect(id: AdvPaymentsSubTab): void {
    this.advPaymentsSubTab = id;
  }

  private setExaminerOfficeIds(ids: string[]): void {
    const uniq = [...new Set(ids.map((x) => String(x).trim()).filter(Boolean))];
    this.extended.controls['examinerOfficeIds'].setValue(uniq);
  }

  private setExaminerIds(ids: string[]): void {
    const uniq = [...new Set(ids.map((x) => String(x).trim()).filter(Boolean))];
    this.extended.controls['examinerIds'].setValue(uniq);
  }

  private setProcessorOfficeIds(ids: string[]): void {
    const uniq = [...new Set(ids.map((x) => String(x).trim()).filter(Boolean))];
    this.extended.controls['processorOfficeIds'].setValue(uniq);
  }

  addSelectedProcessorOffices(left: HTMLSelectElement): void {
    const add = Array.from(left.selectedOptions).map((o) => o.value);
    const cur = (this.extended.controls['processorOfficeIds'].value ?? []) as string[];
    this.setProcessorOfficeIds([...cur, ...add]);
  }

  addAllProcessorOffices(): void {
    const cur = (this.extended.controls['processorOfficeIds'].value ?? []) as string[];
    const more = this.availableProcessorOfficeOptions.map((o) => o.value);
    this.setProcessorOfficeIds([...cur, ...more]);
  }

  removeSelectedProcessorOffices(right: HTMLSelectElement): void {
    const rem = new Set(Array.from(right.selectedOptions).map((o) => o.value));
    const cur = ((this.extended.controls['processorOfficeIds'].value ?? []) as string[]).filter((id) => !rem.has(id));
    this.setProcessorOfficeIds(cur);
  }

  removeAllProcessorOffices(): void {
    this.setProcessorOfficeIds([]);
  }

  private setBankingBankNumbers(nums: string[]): void {
    const uniq = [...new Set(nums.map((x) => String(x).trim()).filter(Boolean))];
    this.extended.controls['bankingBankNumbers'].setValue(uniq);
  }

  addSelectedBanks(left: HTMLSelectElement): void {
    const add = Array.from(left.selectedOptions).map((o) => o.value);
    const cur = (this.extended.controls['bankingBankNumbers'].value ?? []) as string[];
    this.setBankingBankNumbers([...cur, ...add]);
  }

  addAllBanks(): void {
    const cur = (this.extended.controls['bankingBankNumbers'].value ?? []) as string[];
    const more = this.availableBankingOptionsForDualList.map((o) => o.value);
    this.setBankingBankNumbers([...cur, ...more]);
  }

  removeSelectedBanks(right: HTMLSelectElement): void {
    const rem = new Set(Array.from(right.selectedOptions).map((o) => o.value));
    const cur = ((this.extended.controls['bankingBankNumbers'].value ?? []) as string[]).filter((id) => !rem.has(id));
    this.setBankingBankNumbers(cur);
  }

  removeAllBanks(): void {
    this.setBankingBankNumbers([]);
  }

  isLobSelectedForMajorClass(lob: string): boolean {
    const v = (this.queryForm.controls['linesOfBusiness']?.value ?? []) as string[];
    return Array.isArray(v) && v.includes(lob);
  }

  toggleLobOnMajorClass(lob: string, checked: boolean): void {
    const ctl = this.queryForm.controls['linesOfBusiness'];
    const cur = [...((ctl.value ?? []) as string[])];
    if (checked) {
      if (!cur.includes(lob)) {
        cur.push(lob);
      }
    } else {
      const i = cur.indexOf(lob);
      if (i >= 0) {
        cur.splice(i, 1);
      }
    }
    ctl.setValue(cur);
  }

  addSelectedExaminerOffices(left: HTMLSelectElement): void {
    const add = Array.from(left.selectedOptions).map((o) => o.value);
    const cur = (this.extended.controls['examinerOfficeIds'].value ?? []) as string[];
    this.setExaminerOfficeIds([...cur, ...add]);
  }

  addAllExaminerOffices(): void {
    const cur = (this.extended.controls['examinerOfficeIds'].value ?? []) as string[];
    const more = this.availableExaminerOfficeOptions.map((o) => o.value);
    this.setExaminerOfficeIds([...cur, ...more]);
  }

  removeSelectedExaminerOffices(right: HTMLSelectElement): void {
    const rem = new Set(Array.from(right.selectedOptions).map((o) => o.value));
    const cur = ((this.extended.controls['examinerOfficeIds'].value ?? []) as string[]).filter((id) => !rem.has(id));
    this.setExaminerOfficeIds(cur);
  }

  removeAllExaminerOffices(): void {
    this.setExaminerOfficeIds([]);
  }

  addExaminerIdFromDraft(): void {
    const v = (this.examinerIdDraft.value ?? '').trim();
    if (!v) {
      return;
    }
    if (!/^\w+([-+.]\w+)*$/.test(v)) {
      return;
    }
    const cur = [...this.examinerIdsArray];
    if (!cur.includes(v)) {
      cur.push(v);
    }
    this.setExaminerIds(cur);
    this.examinerIdDraft.setValue('');
  }

  removeSelectedExaminerIds(el: HTMLSelectElement): void {
    const rem = new Set(Array.from(el.selectedOptions).map((o) => o.value));
    this.setExaminerIds(this.examinerIdsArray.filter((id) => !rem.has(id)));
  }

  availableStateOptions(kind: StateCodesKind): { label: string; value: string }[] {
    const ids = new Set((this.extended.controls[kind].value ?? []) as string[]);
    return this.stateOptions.filter((o) => !ids.has(String(o.value)));
  }

  restrictedStateOptions(kind: StateCodesKind): { label: string; value: string }[] {
    const ids = new Set((this.extended.controls[kind].value ?? []) as string[]);
    return this.stateOptions.filter((o) => ids.has(String(o.value)));
  }

  private setStateCodes(kind: StateCodesKind, codes: string[]): void {
    const uniq = [...new Set(codes.map((x) => String(x).trim()).filter(Boolean))];
    this.extended.controls[kind].setValue(uniq);
  }

  addSelectedStates(kind: StateCodesKind, left: HTMLSelectElement): void {
    const add = Array.from(left.selectedOptions).map((o) => o.value);
    const cur = (this.extended.controls[kind].value ?? []) as string[];
    this.setStateCodes(kind, [...cur, ...add]);
  }

  addAllStates(kind: StateCodesKind): void {
    const cur = (this.extended.controls[kind].value ?? []) as string[];
    const more = this.availableStateOptions(kind).map((o) => o.value);
    this.setStateCodes(kind, [...cur, ...more]);
  }

  removeSelectedStates(kind: StateCodesKind, right: HTMLSelectElement): void {
    const rem = new Set(Array.from(right.selectedOptions).map((o) => o.value));
    const cur = ((this.extended.controls[kind].value ?? []) as string[]).filter((id) => !rem.has(id));
    this.setStateCodes(kind, cur);
  }

  removeAllStates(kind: StateCodesKind): void {
    this.setStateCodes(kind, []);
  }

  availableVendorOptions(): { label: string; value: string }[] {
    const ids = new Set((this.extended.controls['vendorIds'].value ?? []) as string[]);
    return this.vendorOptions.filter((o) => !ids.has(String(o.value)));
  }

  restrictedVendorOptions(): { label: string; value: string }[] {
    const ids = (this.extended.controls['vendorIds'].value ?? []) as string[];
    const byValue = new Map(this.vendorOptions.map((o) => [String(o.value), o]));
    const seen = new Set<string>();
    const out: { label: string; value: string }[] = [];
    for (const raw of ids) {
      const id = String(raw).trim();
      if (!id || seen.has(id)) {
        continue;
      }
      seen.add(id);
      const o = byValue.get(id);
      out.push({ value: id, label: o?.label ?? id });
    }
    return out;
  }

  private setVendorIds(ids: string[]): void {
    const uniq = [...new Set(ids.map((x) => String(x).trim()).filter(Boolean))];
    this.extended.controls['vendorIds'].setValue(uniq);
  }

  addSelectedVendors(left: HTMLSelectElement): void {
    const add = Array.from(left.selectedOptions).map((o) => o.value);
    const cur = (this.extended.controls['vendorIds'].value ?? []) as string[];
    this.setVendorIds([...cur, ...add]);
  }

  addAllVendors(): void {
    const cur = (this.extended.controls['vendorIds'].value ?? []) as string[];
    const more = this.availableVendorOptions().map((o) => o.value);
    this.setVendorIds([...cur, ...more]);
  }

  removeSelectedVendors(right: HTMLSelectElement): void {
    const rem = new Set(Array.from(right.selectedOptions).map((o) => o.value));
    const cur = ((this.extended.controls['vendorIds'].value ?? []) as string[]).filter((id) => !rem.has(id));
    this.setVendorIds(cur);
  }

  removeAllVendors(): void {
    this.setVendorIds([]);
  }

  async runIcdSearch(): Promise<void> {
    this.icdSearchLoading = true;
    try {
      const prefix =
        (this.icdCodePrefixSelect.value ?? '') + (this.icdCodeBeginsExtra.value ?? '').trim();
      const iv = (this.icdVersionSelect.value ?? '').trim();
      const res = await this.api.query.searchIcd({
        codeBegins: prefix,
        descriptionContains: this.icdDescriptionContains.value ?? '',
        ...(iv === '9' || iv === '10' ? { icdVersion: iv } : {}),
      });
      this.icdSearchResultItems = (res.items ?? []).map((i) => ({
        code: i.code ?? '',
        description: i.description ?? '',
        version: i.version ?? '',
        displayLabel: (i.displayLabel ?? '').trim(),
      }));
      this.icdLeftSelection.clear();
      this.icdRightSelection.clear();
      this.refreshIcdDualListOptions();
    } finally {
      this.icdSearchLoading = false;
    }
  }

  private refreshIcdDualListOptions(): void {
    const inCodes = new Set(
      ((this.extended.controls['icdCodes'].value ?? []) as string[]).map((c) => String(c).trim()).filter(Boolean),
    );
    this.icdDualLeftOptions = this.icdSearchResultItems
      .filter((x) => !inCodes.has(String(x.code ?? '').trim()))
      .map((x) => ({
        value: String(x.code ?? '').trim(),
        label: this.icdSearchRowLabel(x),
      }));
    const codes = ((this.extended.controls['icdCodes'].value ?? []) as string[])
      .map((c) => String(c).trim())
      .filter(Boolean);
    this.icdDualRightOptions = codes.map((c) => ({
      value: c,
      label: this.icdRestrictedRowLabel(c),
    }));
  }

  /** API <c>displayLabel</c> or client fallback (legacy <c>code (vN) - description</c>). */
  private icdSearchRowLabel(x: { code: string; description: string; version: string; displayLabel: string }): string {
    const fromApi = x.displayLabel?.trim();
    if (fromApi) {
      return fromApi;
    }
    return this.formatIcdDisplayLabel(x.code, x.description, x.version);
  }

  private icdRestrictedRowLabel(code: string): string {
    const k = String(code ?? '').trim();
    const fromApi = this.icdDisplayLabelByCode[k]?.trim();
    if (fromApi) {
      return fromApi;
    }
    const desc = this.icdDescriptionByCode[k]?.trim();
    return desc ? this.formatIcdDisplayLabel(k, desc, '') : k;
  }

  /**
   * Edit load only stores ICD codes; resolve full `displayLabel` lines via the same `icd-search` API
   * used by Search (legacy shows `code (vN) - description`).
   */
  private async hydrateIcdDisplayLabelsForSelectedCodes(): Promise<void> {
    const gen = ++this.icdHydrateGen;
    const codes = ((this.extended.controls['icdCodes'].value ?? []) as string[])
      .map((c) => String(c).trim())
      .filter(Boolean);
    const missing = codes.filter((c) => !this.icdDisplayLabelByCode[c]?.trim());
    if (missing.length === 0) {
      return;
    }
    for (const code of missing) {
      if (gen !== this.icdHydrateGen) {
        return;
      }
      try {
        const res = await this.api.query.searchIcd({
          codeBegins: code,
          descriptionContains: '',
        });
        if (gen !== this.icdHydrateGen) {
          return;
        }
        const items = res.items ?? [];
        const row =
          items.find((i) => String(i.code ?? '').trim() === code) ||
          items.find((i) => String(i.code ?? '').trim().startsWith(code));
        if (row) {
          const rc = String(row.code ?? '').trim();
          if (row.description) {
            this.icdDescriptionByCode[code] = row.description;
          }
          const line =
            row.displayLabel?.trim() ||
            this.formatIcdDisplayLabel(row.code, row.description, row.version);
          if (line) {
            this.icdDisplayLabelByCode[code] = line;
          } else if (rc) {
            this.icdDisplayLabelByCode[code] = rc;
          }
        }
      } catch (err) {
        console.error('[Query] ICD label hydrate failed for code', code, err);
      }
    }
    if (gen !== this.icdHydrateGen) {
      return;
    }
    this.refreshIcdDualListOptions();
  }

  private formatIcdDisplayLabel(code: string, description: string, version: string): string {
    const c = (code ?? '').trim();
    const v = (version ?? '').trim();
    const key = v ? `${c} (v${v})` : c;
    const d = (description ?? '').trim();
    return d ? `${key} - ${d}` : key;
  }

  private setIcdCodes(codes: string[]): void {
    const uniq = [...new Set(codes.map((x) => String(x).trim()).filter(Boolean))];
    this.extended.controls['icdCodes'].setValue(uniq);
  }

  addSelectedIcdFromSearch(_event?: Event): void {
    const add = [...this.icdLeftSelection];
    if (add.length === 0) {
      return;
    }
    const cur = (this.extended.controls['icdCodes'].value ?? []) as string[];
    const byCode = new Map(
      this.icdSearchResultItems.map((x) => [String(x.code ?? '').trim(), x] as const),
    );
    for (const code of add) {
      const row = byCode.get(String(code).trim());
      if (row) {
        if (row.description) {
          this.icdDescriptionByCode[code] = row.description;
        }
        const line =
          row.displayLabel?.trim() || this.formatIcdDisplayLabel(row.code, row.description, row.version);
        if (line) {
          this.icdDisplayLabelByCode[code] = line;
        }
      }
    }
    this.setIcdCodes([...cur, ...add]);
    this.icdLeftSelection.clear();
  }

  addAllIcdFromSearch(_event?: Event): void {
    const cur = (this.extended.controls['icdCodes'].value ?? []) as string[];
    const alreadySelected = new Set(cur.map((c) => String(c).trim()).filter(Boolean));
    const more: string[] = [];
    for (const row of this.icdSearchResultItems) {
      const code = String(row.code ?? '').trim();
      if (!code || alreadySelected.has(code)) {
        continue;
      }
      more.push(code);
      if (row.description) {
        this.icdDescriptionByCode[code] = row.description;
      }
      const line =
        row.displayLabel?.trim() || this.formatIcdDisplayLabel(row.code, row.description, row.version);
      if (line) {
        this.icdDisplayLabelByCode[code] = line;
      }
    }
    if (more.length === 0) {
      return;
    }
    this.setIcdCodes([...cur, ...more]);
    this.icdLeftSelection.clear();
  }

  removeSelectedIcd(_event?: Event): void {
    const rem = new Set(this.icdRightSelection);
    if (rem.size === 0) {
      return;
    }
    const cur = ((this.extended.controls['icdCodes'].value ?? []) as string[]).filter((id) => !rem.has(id));
    for (const code of rem) {
      delete this.icdDescriptionByCode[code];
      delete this.icdDisplayLabelByCode[code];
    }
    this.setIcdCodes(cur);
    this.icdRightSelection.clear();
  }

  removeAllIcd(_event?: Event): void {
    this.icdDescriptionByCode = {};
    this.icdDisplayLabelByCode = {};
    this.setIcdCodes([]);
    this.icdRightSelection.clear();
  }

  private async ensureStatesLoaded(cn: number, qk?: number): Promise<void> {
    if (this.hasLoadedParameterResource('states')) {
      return;
    }
    await this.loadStates(cn, qk);
    this.markParameterResourceLoaded('states');
  }

  private async ensureFlagsLoaded(cn: number, qk?: number): Promise<void> {
    if (this.hasLoadedParameterResource('flags')) {
      return;
    }
    await this.loadFlags(cn, qk);
    this.markParameterResourceLoaded('flags');
  }

  private async ensureExaminersLoaded(cn: number): Promise<void> {
    if (this.hasLoadedParameterResource('examiners')) {
      return;
    }
    await this.loadExaminers(cn);
    this.markParameterResourceLoaded('examiners');
  }

  private async ensureBankingLoaded(cn: number, qk?: number): Promise<void> {
    if (this.hasLoadedParameterResource('banking')) {
      return;
    }
    await this.loadBanking(cn, qk);
    this.markParameterResourceLoaded('banking');
  }

  private async ensurePaymentsLoaded(cn: number, qk?: number): Promise<void> {
    if (this.hasLoadedParameterResource('payments')) {
      return;
    }
    await this.loadPayments(cn, qk);
    this.markParameterResourceLoaded('payments');
  }

  private async ensurePaycodesLoaded(cn: number): Promise<void> {
    if (this.hasLoadedParameterResource('paycodes')) {
      return;
    }
    await this.loadPaycodes(cn);
    this.markParameterResourceLoaded('paycodes');
  }

  private async ensureTimeLoaded(cn: number, qk?: number): Promise<void> {
    if (this.hasLoadedParameterResource('time')) {
      return;
    }
    await this.loadTime(cn, qk);
    this.markParameterResourceLoaded('time');
  }

  private async ensureFinancialLoaded(cn: number, qk?: number): Promise<void> {
    if (this.hasLoadedParameterResource('financial')) {
      return;
    }
    await this.loadFinancial(cn, qk);
    this.markParameterResourceLoaded('financial');
  }

  private async ensureLocationsLoaded(cn: number): Promise<void> {
    if (this.hasLoadedParameterResource('locations')) {
      return;
    }
    await this.loadLocations(cn);
    await this.hydrateLocationLabelsFromExtended();
    if (this.locationSelectionMethod.value === 'Structure') {
      await this.ensureStructureRootLoaded();
    }
    this.markParameterResourceLoaded('locations');
  }

  /** PKG_QUERY.GetLocationSelectionType_p codes A / U / S → UI values (legacy Query Wizard). */
  private applyLocationSelectionMethodFromQueryDetail(): void {
    const raw = this.locationSelectionMethodCode;
    if (raw == null || String(raw).trim() === '') {
      return;
    }
    const u = String(raw).trim().toUpperCase();
    const mapped: 'Accounts' | 'Units' | 'Structure' | null =
      u === 'U' ? 'Units' : u === 'S' ? 'Structure' : u === 'A' ? 'Accounts' : null;
    if (mapped) {
      this.locationSelectionMethod.setValue(mapped, { emitEvent: false });
    }
  }

  private async ensureVendorsLoaded(cn: number, qk?: number): Promise<void> {
    if (this.hasLoadedParameterResource('vendors')) {
      return;
    }
    await this.loadVendors(cn, qk);
    this.markParameterResourceLoaded('vendors');
  }

  /** Loads WC subtype option lists once when the section is visible; keeps subtype enable/disable in sync on revisit. */
  private async ensureWcClaimTypesLoaded(): Promise<void> {
    if (!this.wcClaimTypeSectionVisible) {
      return;
    }
    if (this.hasLoadedParameterResource('wcClaimTypes')) {
      this.syncWcClaimSubtypeControlsEnabled();
      return;
    }
    await this.loadWcClaimTypes();
    this.markParameterResourceLoaded('wcClaimTypes');
  }

  private async ensureWcInjuryWorkLoaded(): Promise<void> {
    if (this.hasLoadedParameterResource('wcInjuryWork')) {
      return;
    }
    await this.loadWcInjuryAndWorkParameters();
    this.markParameterResourceLoaded('wcInjuryWork');
  }

  private async refreshPanelData(): Promise<void> {
    const cn = this.contractNumber;
    if (!cn || cn <= 0) {
      return;
    }
    const qk = this.queryUniqueKey ?? undefined;
    switch (this.panelKey) {
      case 'ClaimType':
        await this.ensureWcClaimTypesLoaded();
        break;
      case 'States':
        await this.ensureStatesLoaded(cn, qk);
        break;
      case 'ClaimFlags':
      case 'AdvClaimFlags':
        await this.ensureFlagsLoaded(cn, qk);
        break;
      case 'Examiner':
      case 'ExaminersOffices':
        await this.ensureExaminersLoaded(cn);
        break;
      case 'PaymentsBanks':
        if (!this.hasLoadedParameterResource('paymentsBanksUi')) {
          this.paycodeSearchResultItems = [];
          this.markParameterResourceLoaded('paymentsBanksUi');
        }
        await this.ensureBankingLoaded(cn, qk);
        await this.ensurePaymentsLoaded(cn, qk);
        await this.ensurePaycodesLoaded(cn);
        break;
      case 'BankAccounts':
        await this.ensureBankingLoaded(cn, qk);
        break;
      case 'Payments':
        await this.ensurePaymentsLoaded(cn, qk);
        await this.ensurePaycodesLoaded(cn);
        break;
      case 'TimeTracking':
      case 'AdvTimeTracking':
        await this.ensureTimeLoaded(cn, qk);
        break;
      case 'Financials':
        await this.ensureFinancialLoaded(cn, qk);
        break;
      case 'Locations':
        await this.ensureLocationsLoaded(cn);
        break;
      case 'Claims':
        await Promise.all([this.ensureStatesLoaded(cn, qk), this.ensureVendorsLoaded(cn, qk)]);
        break;
      case 'WorkersComp':
        await this.ensureWcClaimTypesLoaded();
        await this.ensureWcInjuryWorkLoaded();
        break;
      case 'MajorClass':
        break;
      default:
        break;
    }
  }

  private async loadWcClaimTypes(): Promise<void> {
    const cn = this.contractNumber;
    if (!cn || cn <= 0 || !this.wcClaimTypeSectionVisible) {
      return;
    }
    const key = `${cn}|${this.queryUniqueKey ?? 0}`;
    if (this.wcClaimTypesLoadKey === key && this.indemnityOptions.length > 0) {
      this.syncWcClaimSubtypeControlsEnabled();
      return;
    }
    this.wcClaimTypesLoading = true;
    try {
      const res = await this.api.query.getWcClaimTypes(cn, this.queryUniqueKey ?? undefined);
      this.indemnityOptions = (res.indemnityOptions ?? []).map(normalizeWcLobItem);
      this.medicalOptions = (res.medicalOptions ?? []).map(normalizeWcLobItem);
      this.incidentOptions = (res.incidentOptions ?? []).map(normalizeWcLobItem);
      this.liabilityOptions = (res.liabilityOptions ?? []).map(normalizeWcLobItem);
      applyWcClaimTypesSelection(this.extended, res.selection, {
        activateSection: true,
      });
      this.wcClaimTypesLoadKey = key;
      this.syncWcClaimSubtypeControlsEnabled();
    } finally {
      this.wcClaimTypesLoading = false;
    }
  }

  /** Native multi-select + reactive forms: use control.disable() so UI matches (save uses getRawValue). */
  private syncWcClaimSubtypeControlsEnabled(): void {
    const wcg = this.extended.get('wcClaimTypes') as FormGroup;
    if (!wcg) {
      return;
    }
    const pairs: [string, string][] = [
      ['indemnity', 'indemnityIds'],
      ['medical', 'medicalIds'],
      ['incident', 'incidentIds'],
      ['employersLiability', 'liabilityIds'],
    ];
    for (const [cb, idsKey] of pairs) {
      const on = wcg.get(cb)?.value === true;
      const ids = wcg.get(idsKey);
      if (!ids) {
        continue;
      }
      if (on) {
        ids.enable({ emitEvent: false });
      } else {
        ids.disable({ emitEvent: false });
      }
    }
  }

  private async loadStates(contractNumber: number, queryUniqueKey?: number): Promise<void> {
    this.statesLoading = true;
    try {
      const res = await this.api.query.getStates(contractNumber, queryUniqueKey);
      this.stateOptions = (res.states ?? [])
        .map((s: StateItem) => ({
          label: `${s.stateName} (${s.stateCode})`,
          value: s.stateCode,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
      if (queryUniqueKey) {
        this.extended.controls['lossStateCodes'].setValue(
          (res.selectedLossStates ?? []).map((s) => s.stateCode),
          { emitEvent: false },
        );
        this.extended.controls['jurisStateCodes'].setValue(
          (res.selectedJurisStates ?? []).map((s) => s.stateCode),
          { emitEvent: false },
        );
        this.extended.controls['payrollStateCodes'].setValue(
          (res.selectedPayrollStates ?? []).map((s) => s.stateCode),
          { emitEvent: false },
        );
      }
    } finally {
      this.statesLoading = false;
    }
  }

  private async loadFlags(contractNumber: number, queryUniqueKey?: number): Promise<void> {
    this.flagsLoading = true;
    try {
      const res = await this.api.query.getFlags(contractNumber, queryUniqueKey);
      this.syncFlagsToForm(res.flags ?? []);
    } finally {
      this.flagsLoading = false;
    }
  }

  private syncFlagsToForm(flags: QueryFlagItem[]): void {
    const fg = this.extended.get('flags') as FormGroup;
    for (const def of QUERY_CLAIM_FLAG_FIELD_DEFS) {
      if (fg.controls[def.formKey]) {
        fg.controls[def.formKey].setValue('', { emitEvent: false });
      }
    }
    for (const f of flags) {
      const key = queryClaimFlagFormKeyForCode(f.flagCode ?? '');
      if (key && fg.controls[key]) {
        fg.controls[key].setValue(queryClaimFlagFromStorage(f.selectedValue), {
          emitEvent: false,
        });
      }
    }
  }

  private async loadExaminers(contractNumber: number): Promise<void> {
    this.examinersLoading = true;
    try {
      const res = await this.api.query.getExaminers(contractNumber);
      const officeLabel = (o: { officeId: string; officeName: string }) =>
        `${o.officeId} - ${o.officeName}`;
      const sortOfficeRows = (
        list: { officeId: string; officeName: string }[],
      ): { officeId: string; officeName: string }[] =>
        [...list].sort((a, b) =>
          a.officeId.localeCompare(b.officeId, undefined, { numeric: true }),
        );
      this.examinerOfficeOptions = sortOfficeRows(res.examinerOffices ?? []).map((o) => ({
        label: officeLabel(o),
        value: o.officeId,
      }));
      this.processorOfficeOptions = sortOfficeRows(res.processingOffices ?? []).map((o) => ({
        label: officeLabel(o),
        value: o.officeId,
      }));
    } finally {
      this.examinersLoading = false;
    }
  }

  private async loadBanking(contractNumber: number, queryUniqueKey?: number): Promise<void> {
    this.bankingLoading = true;
    try {
      const res = await this.api.query.getBankingOptions(contractNumber, queryUniqueKey);
      const banks = res.banks ?? res.options ?? [];
      this.bankingOptions = banks.map((b: BankingOption) => ({
        label: this.bankingOptionLabel(b),
        value: b.bankNumber,
      }));
      if (queryUniqueKey && res.selectedBanks?.length) {
        this.extended.controls['bankingBankNumbers'].setValue(
          res.selectedBanks.map((b) => b.bankNumber),
          { emitEvent: false },
        );
      }
    } finally {
      this.bankingLoading = false;
    }
  }

  /** Legacy list text: `code - description` (uses API `displayLabel` when present). */
  private bankingOptionLabel(b: BankingOption): string {
    const fromApi = b.displayLabel?.trim();
    if (fromApi) {
      return fromApi;
    }
    const num = (b.bankNumber ?? '').trim();
    const name = (b.bankName ?? '').trim();
    if (!num) {
      return name;
    }
    if (!name) {
      return num;
    }
    return `${num} - ${name}`;
  }

  /** Legacy list text: `code - description` (uses API `displayLabel` when present). */
  private paycodeItemLabel(p: PaycodeItem): string {
    const fromApi = p.displayLabel?.trim();
    if (fromApi) {
      return fromApi;
    }
    const id = p.paycodeId ? String(p.paycodeId).trim() : '';
    const name = (p.paycodeName ?? '').trim();
    if (!id) {
      return name;
    }
    if (!name) {
      return id;
    }
    return `${id} - ${name}`;
  }

  private async loadPayments(contractNumber: number, queryUniqueKey?: number): Promise<void> {
    this.paymentsLoading = true;
    try {
      this.paymentCheckboxControls.clear();
      /** Legacy `QuerywizardSimplePayment.aspx`: checkboxes are fixed; DB only supplies labels/selection. */
      this.ensureLegacyPaymentCheckboxRows();
      const res = await this.api.query.getPaymentOptions(contractNumber, queryUniqueKey);
      this.applyPaymentOptionsFromApi(res, queryUniqueKey);
      this.syncPaymentSelectionsToExtended();
    } finally {
      this.paymentsLoading = false;
    }
  }

  /**
   * Same fixed set as legacy simple payment page (always render, like .aspx — not driven by row count from DB).
   */
  private ensureLegacyPaymentCheckboxRows(): void {
    const defs: { code: string; name: string }[] = [
      { code: 'Historical', name: 'Historical' },
      { code: 'Manual', name: 'Manual' },
      { code: 'Deductibles', name: 'Deductibles' },
      { code: 'Recoveries', name: 'Recoveries' },
      { code: 'Refunds', name: 'Refunds' },
      { code: 'Scheduled', name: 'Scheduled' },
      { code: 'System', name: 'System' },
      { code: 'Voids', name: 'Voids' },
      { code: 'Checks', name: 'Checks' },
      { code: 'Vouchers', name: 'Vouchers' },
    ];
    const prevByCode = new Map(this.paymentRows.map((r) => [r.code, r]));
    const saved = (this.extended.get('paymentSelections')?.value ?? []) as {
      code: string;
      selected: boolean | string;
    }[];
    const savedByNorm = new Map(
      saved.map((s) => [s.code.trim().toLowerCase(), this.normalizePaymentSelected(s.selected)] as const),
    );
    this.paymentRows = defs.map((d) => {
      const prev = prevByCode.get(d.code);
      let selected = this.normalizePaymentSelected(prev?.selected);
      const fromForm = savedByNorm.get(d.code.toLowerCase());
      if (fromForm !== undefined) {
        selected = fromForm;
      }
      if (d.code === 'Deductibles' && savedByNorm.has('deductables')) {
        selected = savedByNorm.get('deductables')!;
      }
      return { code: d.code, name: d.name, selected };
    });
  }

  /**
   * Legacy `Payments` ctor `Init()` when `!Query.IsEditing` (Payments.cs):
   * Historical, Manual, Refunds, System, Voids + document type Checks; others off.
   * Used when GETQUERYPAYMENTS_P returns no row (e.g. new query, including Banking).
   */
  private legacyNewQueryPaymentTypeDocumentDefaults(): PaymentOption[] {
    return [
      { code: 'Historical', name: 'Historical', selectedValue: 'Y' },
      { code: 'Manual', name: 'Manual', selectedValue: 'Y' },
      { code: 'Refunds', name: 'Refunds', selectedValue: 'Y' },
      { code: 'System', name: 'System', selectedValue: 'Y' },
      { code: 'Voids', name: 'Voids', selectedValue: 'Y' },
      { code: 'Checks', name: 'Checks', selectedValue: 'Y' },
    ];
  }

  /** Overlay GETQUERYPAYMENTS_P / JSON onto the fixed rows (labels + Y/N). */
  private applyPaymentOptionsFromApi(
    res: PaymentOptionsResponse,
    queryUniqueKey?: number,
  ): void {
    let raw = this.normalizePaymentOptionsPayload(res);
    if (
      raw.length === 0 &&
      !(queryUniqueKey !== undefined && queryUniqueKey !== null && queryUniqueKey > 0)
    ) {
      raw = this.legacyNewQueryPaymentTypeDocumentDefaults();
    }
    const byNorm = new Map(this.paymentRows.map((r) => [r.code.trim().toLowerCase(), r] as const));
    for (const p of raw) {
      const code = String(p.code ?? '').trim();
      if (!code) {
        continue;
      }
      const yn = (p.selectedValue ?? 'N').toUpperCase() === 'Y';
      const label = String(p.name ?? p.code ?? '').trim() || code;
      let row = byNorm.get(code.toLowerCase());
      if (!row && code.toLowerCase() === 'deductables') {
        row = byNorm.get('deductibles');
      }
      if (row) {
        row.name = label;
        row.selected = yn;
      }
    }
  }

  /**
   * Prefer combined `options`; if empty, merge `paymentTypes` + `documentTypes` (same as API split).
   * Maps PascalCase JSON fields if present.
   */
  private normalizePaymentOptionsPayload(res: {
    options?: PaymentOption[];
    paymentTypes?: PaymentOption[];
    documentTypes?: PaymentOption[];
  }): PaymentOption[] {
    const pick = (p: PaymentOption & { Code?: string; Name?: string; SelectedValue?: string }) => ({
      code: String(p.code ?? p.Code ?? '').trim(),
      name: String(p.name ?? p.Name ?? p.code ?? p.Code ?? '').trim(),
      selectedValue: p.selectedValue ?? p.SelectedValue ?? 'N',
    });

    const opts = (res.options ?? []).map(pick).filter((p) => p.code);
    if (opts.length > 0) {
      return opts;
    }
    const pt = (res.paymentTypes ?? []).map(pick).filter((p) => p.code);
    const dt = (res.documentTypes ?? []).map(pick).filter((p) => p.code);
    return [...pt, ...dt];
  }

  private syncPaymentSelectionsToExtended(): void {
    const rows = this.paymentRows.map((r) => ({ code: r.code, selected: r.selected }));
    this.extended.get('paymentSelections')?.setValue(rows, { emitEvent: false });
  }

  setPaymentSelected(code: string, checked: boolean): void {
    const row = this.paymentRows.find((r) => r.code === code);
    if (row) {
      row.selected = checked;
    }
    this.syncPaymentSelectionsToExtended();
  }

  /** API uses Y/N; form and save payload use boolean. */
  private normalizePaymentSelected(raw: boolean | string | undefined): boolean {
    if (typeof raw === 'boolean') {
      return raw;
    }
    if (typeof raw === 'string') {
      return raw.trim().toUpperCase() === 'Y';
    }
    return false;
  }

  /** Legacy: series checkboxes drive `paycodeSeries`; restricted list shows one row per series (e.g. `2 - All 200 Series`). */
  setPaySeriesSelected(digit: string, selected: boolean): void {
    const g = this.extended.get('paycodeSeries');
    const cur = [...((g?.value ?? []) as string[])];
    const i = cur.indexOf(digit);
    if (selected && i < 0) {
      cur.push(digit);
    } else if (!selected && i >= 0) {
      cur.splice(i, 1);
    }
    g?.setValue(cur, { emitEvent: false });

    if (selected) {
      const expr = (this.extended.controls['paycodeExpressions'].value ?? []) as string[];
      const next = expr.filter((id) => !this.paycodeIdCoveredBySeriesDigit(String(id), digit));
      if (next.length !== expr.length) {
        this.extended.controls['paycodeExpressions'].setValue(next, { emitEvent: false });
      }
    }
  }

  isPaySeriesSelected(digit: string): boolean {
    return ((this.extended.get('paycodeSeries')?.value ?? []) as string[]).includes(digit);
  }

  async runPaycodeSearch(): Promise<void> {
    const cn = this.contractNumber;
    if (!cn || cn <= 0) {
      return;
    }
    this.paycodesLoading = true;
    try {
      const res = await this.api.query.getPaycodes(cn, {
        paycodePrefix: this.paycodeSearchPrefix.value?.trim() ?? '',
        descriptionContains: this.paycodeSearchDescription.value?.trim() ?? '',
        performSearch: true,
      });
      this.paycodeSearchResultItems = res.paycodes?.length ? res.paycodes : res.searchResults ?? [];
    } finally {
      this.paycodesLoading = false;
    }
  }

  addSelectedPaycodesFromSearch(sel: HTMLSelectElement): void {
    const add = Array.from(sel.selectedOptions).map((o) => o.value);
    const cur = new Set((this.extended.controls['paycodeExpressions'].value ?? []) as string[]);
    add.forEach((id) => cur.add(id));
    this.extended.controls['paycodeExpressions'].setValue([...cur], { emitEvent: false });
  }

  addAllPaycodesFromSearch(): void {
    const more = this.availablePaycodeSearchDualList.map((o) => o.value);
    const cur = new Set((this.extended.controls['paycodeExpressions'].value ?? []) as string[]);
    more.forEach((id) => cur.add(id));
    this.extended.controls['paycodeExpressions'].setValue([...cur], { emitEvent: false });
  }

  removeSelectedPaycodesFromRestricted(sel: HTMLSelectElement): void {
    const selected = Array.from(sel.selectedOptions).map((o) => o.value);
    let series = [...((this.extended.get('paycodeSeries')?.value ?? []) as string[])];
    const expr = new Set((this.extended.controls['paycodeExpressions'].value ?? []) as string[]);
    for (const v of selected) {
      if (series.includes(v)) {
        series = series.filter((d) => d !== v);
      } else {
        expr.delete(v);
      }
    }
    this.extended.get('paycodeSeries')?.setValue(series, { emitEvent: false });
    this.extended.controls['paycodeExpressions'].setValue([...expr], { emitEvent: false });
  }

  removeAllPaycodesFromRestricted(): void {
    this.extended.controls['paycodeExpressions'].setValue([], { emitEvent: false });
    this.extended.get('paycodeSeries')?.setValue([], { emitEvent: false });
  }

  addSelectedTimeTypes(sel: HTMLSelectElement): void {
    const add = Array.from(sel.selectedOptions).map((o) => o.value);
    const cur = new Set((this.timeGroup.controls['benefitTypes'].value ?? []) as string[]);
    for (const id of add) {
      cur.add(id);
    }
    this.timeGroup.controls['benefitTypes'].setValue([...cur]);
  }

  addAllTimeTypes(): void {
    const more = this.timeTypeAvailableDualList.map((o) => o.value);
    const cur = new Set((this.timeGroup.controls['benefitTypes'].value ?? []) as string[]);
    for (const id of more) {
      cur.add(id);
    }
    this.timeGroup.controls['benefitTypes'].setValue([...cur]);
  }

  removeSelectedTimeTypes(sel: HTMLSelectElement): void {
    const rem = new Set(Array.from(sel.selectedOptions).map((o) => o.value));
    const cur = ((this.timeGroup.controls['benefitTypes'].value ?? []) as string[]).filter((id) => !rem.has(id));
    this.timeGroup.controls['benefitTypes'].setValue(cur);
  }

  removeAllTimeTypes(): void {
    this.timeGroup.controls['benefitTypes'].setValue([]);
  }

  addSelectedTimeStatuses(sel: HTMLSelectElement): void {
    const add = Array.from(sel.selectedOptions).map((o) => o.value);
    const cur = new Set((this.timeGroup.controls['benefitStatuses'].value ?? []) as string[]);
    for (const id of add) {
      cur.add(id);
    }
    this.timeGroup.controls['benefitStatuses'].setValue([...cur]);
  }

  addAllTimeStatuses(): void {
    const more = this.timeStatusAvailableDualList.map((o) => o.value);
    const cur = new Set((this.timeGroup.controls['benefitStatuses'].value ?? []) as string[]);
    for (const id of more) {
      cur.add(id);
    }
    this.timeGroup.controls['benefitStatuses'].setValue([...cur]);
  }

  removeSelectedTimeStatuses(sel: HTMLSelectElement): void {
    const rem = new Set(Array.from(sel.selectedOptions).map((o) => o.value));
    const cur = ((this.timeGroup.controls['benefitStatuses'].value ?? []) as string[]).filter((id) => !rem.has(id));
    this.timeGroup.controls['benefitStatuses'].setValue(cur);
  }

  removeAllTimeStatuses(): void {
    this.timeGroup.controls['benefitStatuses'].setValue([]);
  }

  addSelectedTimeReasons(sel: HTMLSelectElement): void {
    const add = Array.from(sel.selectedOptions).map((o) => o.value);
    const cur = new Set((this.timeGroup.controls['benefitReasons'].value ?? []) as string[]);
    for (const id of add) {
      cur.add(id);
    }
    this.timeGroup.controls['benefitReasons'].setValue([...cur]);
  }

  addAllTimeReasons(): void {
    const more = this.timeReasonAvailableDualList.map((o) => o.value);
    const cur = new Set((this.timeGroup.controls['benefitReasons'].value ?? []) as string[]);
    for (const id of more) {
      cur.add(id);
    }
    this.timeGroup.controls['benefitReasons'].setValue([...cur]);
  }

  removeSelectedTimeReasons(sel: HTMLSelectElement): void {
    const rem = new Set(Array.from(sel.selectedOptions).map((o) => o.value));
    const cur = ((this.timeGroup.controls['benefitReasons'].value ?? []) as string[]).filter((id) => !rem.has(id));
    this.timeGroup.controls['benefitReasons'].setValue(cur);
  }

  removeAllTimeReasons(): void {
    this.timeGroup.controls['benefitReasons'].setValue([]);
  }

  private async loadTime(contractNumber: number, queryUniqueKey?: number): Promise<void> {
    this.timeLoading = true;
    try {
      const res = await this.api.query.getTimeOptions(contractNumber, queryUniqueKey);
      const toOpts = (list: TimeTrackingOption[] | undefined) =>
        (list ?? []).map((x) => ({ label: x.name, value: x.code }));
      this.timeTypeSelOpts = toOpts(res.timeTypes ?? res.options);
      this.timeStatusSelOpts = toOpts(res.timeStatuses);
      this.timeReasonSelOpts = toOpts(res.timeReasons);
      const tg = this.timeGroup;
      if (res.benefitBegin != null && res.benefitBegin !== '') {
        tg.controls['benefitBegin'].setValue(new Date(res.benefitBegin), { emitEvent: false });
      }
      if (res.benefitEnd != null && res.benefitEnd !== '') {
        tg.controls['benefitEnd'].setValue(new Date(res.benefitEnd), { emitEvent: false });
      }
      if (queryUniqueKey) {
        tg.controls['benefitTypes'].setValue(
          (res.selectedTimeTypes ?? []).map((x) => x.code),
          { emitEvent: false },
        );
        tg.controls['benefitStatuses'].setValue(
          (res.selectedTimeStatuses ?? []).map((x) => x.code),
          { emitEvent: false },
        );
        tg.controls['benefitReasons'].setValue(
          (res.selectedTimeReasons ?? []).map((x) => x.code),
          { emitEvent: false },
        );
      }
    } finally {
      this.timeLoading = false;
    }
  }

  private async loadFinancial(contractNumber: number, queryUniqueKey?: number): Promise<void> {
    this.financialLoading = true;
    try {
      const res = await this.api.query.getFinancialOptions(contractNumber, queryUniqueKey);
      applyFinancialOptionsFromApi(this.extended, res.options ?? [], {
        activateSection: true,
      });
    } finally {
      this.financialLoading = false;
    }
  }

  private async loadLocations(contractNumber: number): Promise<void> {
    this.locationsLoading = true;
    try {
      const res = await this.api.query.getLocations(contractNumber);
      this.accountOptions = (res.accounts ?? []).map((a) => ({
        label: a.accountName ?? a.accountId,
        value: String(a.accountId),
      }));
      this.unitsByAccountCache.clear();
      this.expandedAccounts = [];
      this.unitsSearchResults = [];
    } finally {
      this.locationsLoading = false;
    }
  }

  /** Load unit labels for keys already in the form (e.g. opening a saved query). */
  private async hydrateLocationLabelsFromExtended(): Promise<void> {
    const keys = this.accountUnitIdsArray;
    if (keys.length === 0) {
      return;
    }
    const accountIds = new Set<string>();
    for (const k of keys) {
      const p = k.indexOf('|');
      if (p > 0) {
        accountIds.add(k.slice(0, p));
      }
    }
    for (const aid of accountIds) {
      await this.ensureUnitsForAccount(aid);
      const opts = this.unitsByAccountCache.get(aid) ?? [];
      for (const o of opts) {
        if (keys.includes(o.value)) {
          this.unitLabelByCompositeKey.set(o.value, o.label);
        }
      }
    }
  }

  get accountUnitIdsArray(): string[] {
    return (this.extended.controls['accountUnitIds'].value ?? []) as string[];
  }

  get availableLocationSearchOptions(): { label: string; value: string }[] {
    const sel = new Set(this.accountUnitIdsArray);
    return (this.unitsSearchResults ?? [])
      .filter((u) => !sel.has(`${u.accountId}|${u.unitId}`))
      .map((u) => ({
        value: `${u.accountId}|${u.unitId}`,
        label: this.formatLocationUnitLabel(u),
      }));
  }

  get restrictedLocationUnitOptions(): { label: string; value: string }[] {
    return this.accountUnitIdsArray.map((k) => ({
      value: k,
      label: this.unitLabelByCompositeKey.get(k) ?? k,
    }));
  }

  formatLocationUnitLabel(u: LocationUnitItem): string {
    const sc = u.structureCode ? ` — ${u.structureCode}` : '';
    return `${u.unitName ?? ''} (${u.unitId}) — Account ${u.accountId}${sc}`;
  }

  onLocationAccountUnitsSelectionChange(ids: string[]): void {
    this.setAccountUnitIds(ids);
  }

  async toggleLocationAccountExpand(accountId: string): Promise<void> {
    const i = this.expandedAccounts.indexOf(accountId);
    if (i >= 0) {
      this.expandedAccounts.splice(i, 1);
    } else {
      this.expandedAccounts.push(accountId);
      await this.ensureUnitsForAccount(accountId);
    }
  }

  async ensureUnitsForAccount(accountId: string): Promise<void> {
    if (this.unitsByAccountCache.has(accountId)) {
      return;
    }
    const cn = this.contractNumber;
    if (!cn) {
      return;
    }
    const num = Number(accountId);
    if (num <= 0 || Number.isNaN(num)) {
      return;
    }
    this.accountUnitsLoading = { ...this.accountUnitsLoading, [accountId]: true };
    try {
      const res = await this.api.query.getLocations(cn, { accountNumber: num });
      const opts = (res.units ?? []).map((u) => {
        const v = `${u.accountId}|${u.unitId}`;
        const lbl = this.formatLocationUnitLabel(u);
        this.unitLabelByCompositeKey.set(v, lbl);
        return { label: lbl, value: v };
      });
      this.unitsByAccountCache.set(accountId, opts);
    } finally {
      const next = { ...this.accountUnitsLoading };
      delete next[accountId];
      this.accountUnitsLoading = next;
    }
  }

  async addAllUnitsForAccount(accountId: string): Promise<void> {
    await this.ensureUnitsForAccount(accountId);
    const opts = this.unitsByAccountCache.get(accountId) ?? [];
    const cur = new Set(this.accountUnitIdsArray);
    for (const o of opts) {
      cur.add(o.value);
      this.unitLabelByCompositeKey.set(o.value, o.label);
    }
    this.extended.controls['accountUnitIds'].setValue([...cur]);
  }

  async runLocationUnitSearch(): Promise<void> {
    const cn = this.contractNumber;
    if (!cn) {
      return;
    }
    this.locationSearchLoading = true;
    try {
      const res = await this.api.query.getLocations(cn, {
        searchAllAccounts: true,
        unitNumber: this.locationSearchUnitNumber.value?.trim() || undefined,
        structureCode: this.locationSearchStructCode.value?.trim() || undefined,
        locationName: this.locationSearchLocationName.value?.trim() || undefined,
        address: this.locationSearchAddress.value?.trim() || undefined,
        city: this.locationSearchCity.value?.trim() || undefined,
        state: this.locationSearchState.value?.trim() || undefined,
        zip: this.locationSearchZip.value?.trim() || undefined,
      });
      this.unitsSearchResults = res.units ?? [];
      for (const u of this.unitsSearchResults) {
        const k = `${u.accountId}|${u.unitId}`;
        this.unitLabelByCompositeKey.set(k, this.formatLocationUnitLabel(u));
      }
    } finally {
      this.locationSearchLoading = false;
    }
  }

  private setAccountUnitIds(ids: string[]): void {
    const uniq = [...new Set(ids.map((x) => String(x).trim()).filter(Boolean))];
    this.extended.controls['accountUnitIds'].setValue(uniq);
  }

  addSelectedLocationUnitsFromSearch(left: HTMLSelectElement): void {
    const add = Array.from(left.selectedOptions).map((o) => o.value);
    const cur = [...this.accountUnitIdsArray];
    for (const k of add) {
      if (!cur.includes(k)) {
        cur.push(k);
      }
    }
    this.setAccountUnitIds(cur);
  }

  addAllLocationUnitsFromSearch(): void {
    const cur = [...this.accountUnitIdsArray];
    for (const o of this.availableLocationSearchOptions) {
      if (!cur.includes(o.value)) {
        cur.push(o.value);
      }
    }
    this.setAccountUnitIds(cur);
  }

  removeSelectedLocationUnitsFromSearch(right: HTMLSelectElement): void {
    const rem = new Set(Array.from(right.selectedOptions).map((o) => o.value));
    this.setAccountUnitIds(this.accountUnitIdsArray.filter((id) => !rem.has(id)));
  }

  removeAllLocationUnitsFromSearch(): void {
    this.setAccountUnitIds([]);
  }

  onWcLobSubTabSelect(id: WcLobSubTab): void {
    this.wcLobSubTab = id;
    if (id === 'claimType') {
      void this.loadWcClaimTypes();
    }
    if (id === 'workStatus') {
      void this.loadQueryWorkStatusFromApi();
    }
  }

  onDsLobSubTabSelect(id: DsLobSubTab): void {
    this.dsLobSubTab = id;
    if (id === 'workStatus') {
      void this.loadQueryWorkStatusFromApi();
    }
  }

  /** Load work-status + terminated reasons (contract-scoped; refetch when Work Status tab opens). */
  private async loadQueryWorkStatusFromApi(): Promise<void> {
    const cn = this.contractNumber;
    if (!cn || cn <= 0) {
      return;
    }
    const qk = this.queryUniqueKey;
    try {
      const qw = await this.api.query.getQueryWorkParameters(qk ?? undefined);
      this.queryWorkTerminatedReasonOptions = (qw.terminatedReasons ?? []).map((r) => ({
        label: r.label,
        value: r.value,
      }));
      if (!this.extended.get('includeQueryWork')?.value) {
        applyQueryWorkFromApi(this.extended, qw.work, { activateSection: true });
      }
    } catch {
      /* optional */
    }
  }

  private async loadWcInjuryAndWorkParameters(): Promise<void> {
    const cn = this.contractNumber;
    if (!cn || cn <= 0) {
      return;
    }
    const qk = this.queryUniqueKey;
    const injuryKey = `${cn}|${qk ?? 0}|inj`;
    if (this.wcInjurySelectionLoadKey !== injuryKey) {
      try {
        const inj = await this.api.query.getWcInjuryParameters(cn, qk ?? undefined);
        for (const x of [...inj.selectedCauses, ...inj.selectedNatures, ...inj.selectedTargets]) {
          const id = String(x.id ?? '').trim();
          if (!id) {
            continue;
          }
          this.wcInjuryLabelById.set(id, this.wcInjuryDisplayLabel(x));
        }
        applyWcInjuryParametersFromApi(this.extended, inj, {
          activateSection: true,
        });
        this.wcInjurySelectionLoadKey = injuryKey;
      } catch (err) {
        console.error('[Query] getWcInjuryParameters failed (WC Injury Codes will be empty until this succeeds)', err);
      }
    }
    await this.loadQueryWorkStatusFromApi();
  }

  idsForWcInjuryKind(kind: 'cause' | 'nature' | 'part'): string[] {
    let raw: unknown[] = [];
    if (kind === 'cause') {
      raw = (this.extended.controls['wcInjuryCauseIds'].value ?? []) as unknown[];
    } else if (kind === 'nature') {
      raw = (this.extended.controls['wcInjuryNatureIds'].value ?? []) as unknown[];
    } else {
      raw = (this.extended.controls['wcInjuryPartTargetIds'].value ?? []) as unknown[];
    }
    return [...new Set(raw.map((id) => String(id ?? '').trim()).filter(Boolean))];
  }

  /** Legacy list boxes show code and description separated by " - ". Prefer structured fields when present. */
  private wcInjuryDisplayLabel(row: QueryWcInjuryCodeItem): string {
    const pre = row.displayLabel?.trim();
    if (pre) {
      return pre;
    }
    const code = row.code?.trim();
    const desc = row.description?.trim();
    if (code && desc) {
      return `${code} - ${desc}`;
    }
    if (code) {
      return code;
    }
    if (desc) {
      return desc;
    }
    const fallback = row.label?.trim();
    if (fallback) {
      return fallback;
    }
    return row.id || '—';
  }

  availableWcInjurySearchOptions(kind: 'cause' | 'nature' | 'part'): { label: string; value: string }[] {
    const rows =
      kind === 'cause'
        ? this.wcInjuryCauseSearchRows
        : kind === 'nature'
          ? this.wcInjuryNatureSearchRows
          : this.wcInjuryPartSearchRows;
    const sel = new Set(this.idsForWcInjuryKind(kind));
    return (rows ?? [])
      .filter((r) => r.id && !sel.has(String(r.id)))
      .map((r) => ({ value: r.id, label: this.wcInjuryDisplayLabel(r) }));
  }

  restrictedWcInjuryOptions(kind: 'cause' | 'nature' | 'part'): { label: string; value: string }[] {
    return this.idsForWcInjuryKind(kind).map((id) => ({
      value: id,
      label: this.wcInjuryLabelById.get(id) ?? id,
    }));
  }

  private findWcInjurySearchRow(kind: 'cause' | 'nature' | 'part', id: string): QueryWcInjuryCodeItem | undefined {
    const rows =
      kind === 'cause'
        ? this.wcInjuryCauseSearchRows
        : kind === 'nature'
          ? this.wcInjuryNatureSearchRows
          : this.wcInjuryPartSearchRows;
    const sid = String(id ?? '').trim();
    return rows.find((r) => String(r.id ?? '').trim() === sid);
  }

  private setWcInjuryIds(kind: 'cause' | 'nature' | 'part', ids: string[]): void {
    const uniq = [...new Set(ids.map((x) => String(x).trim()).filter(Boolean))];
    if (kind === 'cause') {
      this.extended.controls['wcInjuryCauseIds'].setValue(uniq);
    } else if (kind === 'nature') {
      this.extended.controls['wcInjuryNatureIds'].setValue(uniq);
    } else {
      this.extended.controls['wcInjuryPartTargetIds'].setValue(uniq);
    }
    this.extended.get('includeWcInjury')?.setValue(true, { emitEvent: false });
  }

  async runWcInjurySearch(kind: 'cause' | 'nature' | 'part'): Promise<void> {
    const cn = this.contractNumber;
    if (!cn) {
      return;
    }
    const code =
      kind === 'cause'
        ? this.injuryCauseCode.value
        : kind === 'nature'
          ? this.injuryNatureCode.value
          : this.injuryPartCode.value;
    const desc =
      kind === 'cause'
        ? this.injuryCauseDesc.value
        : kind === 'nature'
          ? this.injuryNatureDesc.value
          : this.injuryPartDesc.value;
    this.wcInjurySearchLoading[kind] = true;
    try {
      const res = await this.api.query.searchWcInjuryCodes({
        contractNumber: cn,
        kind: kind === 'part' ? 'part' : kind,
        code: code?.trim() ?? '',
        description: desc?.trim() ?? '',
      });
      const items = [...(res.items ?? [])].sort((a, b) =>
        (a.code ?? a.id ?? '').localeCompare(b.code ?? b.id ?? '', undefined, {
          sensitivity: 'base',
          numeric: true,
        }),
      );
      if (kind === 'cause') {
        this.wcInjuryCauseSearchRows = items;
      } else if (kind === 'nature') {
        this.wcInjuryNatureSearchRows = items;
      } else {
        this.wcInjuryPartSearchRows = items;
      }
      for (const i of items) {
        this.wcInjuryLabelById.set(i.id, this.wcInjuryDisplayLabel(i));
      }
      this.extended.get('includeWcInjury')?.setValue(true, { emitEvent: false });
    } finally {
      this.wcInjurySearchLoading[kind] = false;
    }
  }

  addSelectedWcInjury(kind: 'cause' | 'nature' | 'part', left: HTMLSelectElement): void {
    const add = Array.from(left.selectedOptions).map((o) => o.value);
    const cur = [...this.idsForWcInjuryKind(kind)];
    for (const id of add) {
      if (!cur.includes(id)) {
        cur.push(id);
      }
      const row = this.findWcInjurySearchRow(kind, id);
      if (row) {
        this.wcInjuryLabelById.set(id, this.wcInjuryDisplayLabel(row));
      }
    }
    this.setWcInjuryIds(kind, cur);
  }

  addAllWcInjurySearch(kind: 'cause' | 'nature' | 'part'): void {
    const cur = [...this.idsForWcInjuryKind(kind)];
    for (const o of this.availableWcInjurySearchOptions(kind)) {
      if (!cur.includes(o.value)) {
        cur.push(o.value);
      }
      this.wcInjuryLabelById.set(o.value, o.label);
    }
    this.setWcInjuryIds(kind, cur);
  }

  removeSelectedWcInjury(kind: 'cause' | 'nature' | 'part', right: HTMLSelectElement): void {
    const rem = new Set(Array.from(right.selectedOptions).map((o) => o.value));
    this.setWcInjuryIds(kind, this.idsForWcInjuryKind(kind).filter((id) => !rem.has(id)));
  }

  removeAllWcInjury(kind: 'cause' | 'nature' | 'part'): void {
    this.setWcInjuryIds(kind, []);
  }

  private mapStructureNodeToTreeNode(n: CorpStructNodeItem, parent: TreeNode | null): TreeNode {
    const tn = new TreeNode();
    const code = n.locCode ? ` (${n.locCode})` : '';
    tn.label = `${n.locationName ?? ''}${code}`.trim() || `Node ${n.nodeId}`;
    tn.value = n.nodeId;
    if (parent) {
      tn.parent = parent;
    }
    tn.loaded = !n.hasChildren;
    tn.children = n.hasChildren ? undefined : [];
    return tn;
  }

  private async ensureStructureRootLoaded(): Promise<void> {
    const cn = this.contractNumber;
    if (!cn || cn <= 0) {
      return;
    }
    if (this.structureRootLoadedContract === cn) {
      return;
    }
    this.structureTreeLoading = true;
    try {
      const res = await this.api.filtersCriteria.getCorpStruct({
        contractNumber: cn,
        parentNodeId: 0,
      });
      this.structureTreeNodes = (res.nodes ?? []).map((n) => this.mapStructureNodeToTreeNode(n, null));
      this.structureRootLoadedContract = cn;
    } catch {
      this.structureTreeNodes = [];
    } finally {
      this.structureTreeLoading = false;
    }
  }

  onStructureTreeCheckboxChange(selected: TreeNode | TreeNode[]): void {
    const arr = Array.isArray(selected) ? selected : selected ? [selected] : [];
    const ids = arr
      .map((n) => (typeof n.value === 'number' ? n.value : Number(n.value)))
      .filter((id) => !Number.isNaN(id) && id > 0);
    this.extended.controls['structureNodeIds'].setValue(ids);
  }

  async onStructureTreeExpand(node: TreeNode): Promise<void> {
    const cn = this.contractNumber;
    if (!cn || node.loaded) {
      return;
    }
    node.loading = true;
    try {
      const res = await this.api.filtersCriteria.getCorpStruct({
        contractNumber: cn,
        parentNodeId: node.value as number,
      });
      const children = (res.nodes ?? []).map((n) => this.mapStructureNodeToTreeNode(n, node));
      node.children = children;
      node.loaded = true;
      node.leaf = children.length === 0;
    } catch {
      node.loaded = true;
      node.children = [];
      node.leaf = true;
    } finally {
      node.loading = false;
      this.structureTreeNodes = [...this.structureTreeNodes];
    }
  }

  async runStructureSearch(): Promise<void> {
    const cn = this.contractNumber;
    if (!cn || cn <= 0) {
      return;
    }
    this.structureSearchLoading = true;
    try {
      const res = await this.api.filtersCriteria.searchCorpStruct({
        contractNumber: cn,
        searchText: this.structureSearchName.value?.trim() ?? '',
        includeIntermediateLevels: this.structureSearchIncludeIntermediate.value ?? false,
      });
      this.structureSearchRows = res.nodes ?? [];
    } catch {
      this.structureSearchRows = [];
    } finally {
      this.structureSearchLoading = false;
    }
  }

  private async loadVendors(contractNumber: number, queryUniqueKey?: number): Promise<void> {
    this.vendorsLoading = true;
    try {
      const res = await this.api.query.getVendors(contractNumber, queryUniqueKey);
      const mapped = (res.vendors ?? []).map((v) => ({
        label: (v.vendorName ?? '').trim() || String(v.vendorId),
        value: String(v.vendorId).trim(),
      }));
      const byId = new Map<string, { label: string; value: string }>();
      for (const o of mapped) {
        if (!o.value || byId.has(o.value)) {
          continue;
        }
        byId.set(o.value, o);
      }
      this.vendorOptions = [...byId.values()].sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
      );
      if (queryUniqueKey && res.selectedVendors?.length) {
        const sel: string[] = [];
        const seenSel = new Set<string>();
        for (const v of res.selectedVendors) {
          const id = String(v.vendorId).trim();
          if (!id || seenSel.has(id)) {
            continue;
          }
          seenSel.add(id);
          sel.push(id);
        }
        this.extended.controls['vendorIds'].setValue(sel, { emitEvent: false });
      }
    } finally {
      this.vendorsLoading = false;
    }
  }

  private async loadPaycodes(contractNumber: number): Promise<void> {
    this.paycodesLoading = true;
    try {
      const res = await this.api.query.getPaycodes(contractNumber, {
        queryUniqueKey: this.queryUniqueKey ?? undefined,
      });
      const selected = res.selectedPaycodes ?? [];
      const list = res.paycodes ?? [];
      const byId = new Map<string, PaycodeItem>();
      for (const p of [...list, ...selected]) {
        if (p.paycodeId) {
          byId.set(String(p.paycodeId), p);
        }
      }
      this.paycodeOptions = [...byId.values()].map((p) => ({
        label: this.paycodeItemLabel(p),
        value: String(p.paycodeId),
      }));
      if (this.queryUniqueKey && this.queryUniqueKey > 0) {
        const series = (res.selectedSeries ?? []).map(String);
        const expr = selected.map((p) => String(p.paycodeId)).filter(Boolean);
        const pruned = expr.filter(
          (id) => !series.some((d) => this.paycodeIdCoveredBySeriesDigit(id, d)),
        );
        this.extended.controls['paycodeExpressions'].setValue(pruned, { emitEvent: false });
        this.extended.controls['paycodeSeries'].setValue(series, { emitEvent: false });
      }
    } finally {
      this.paycodesLoading = false;
    }
  }
}
